import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { MultimodalLiveClient } from '../../../lib/multimodal-live-client';
import { useLiveAPIContext } from './LiveAPIContext';
import { InterviewDetails, InterviewFeedback, Message } from '../../../types/interviews';
// Import uuid without type checking
// @ts-ignore - This suppresses the TypeScript warning about missing types
import { v4 as uuidv4 } from 'uuid';
import { GenerativeContentBlob } from '@google/generative-ai';
import { AudioStreamer } from '../../../lib/audio-streamer';
// Import the configuration constants
import { USER_TRANSCRIPTION_TYPE, ACTIVE_TRANSCRIPTION_TYPE } from '../../../constants/common';
import FeedbackService from '../../../services/FeedbackService';
// SpeechRecognition type definitions using proper TypeScript approach
interface SpeechRecognitionEventMap {
  audiostart: Event;
  audioend: Event;
  end: Event;
  error: SpeechRecognitionErrorEvent;
  nomatch: SpeechRecognitionEvent;
  result: SpeechRecognitionEvent;
  soundstart: Event;
  soundend: Event;
  speechstart: Event;
  speechend: Event;
  start: Event;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  [index: number]: SpeechRecognitionResult;
  length: number;
}

interface SpeechRecognitionResult {
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
  length: number;
}

interface SpeechRecognitionAlternative {
  confidence: number;
  transcript: string;
}

interface SpeechGrammar {
  src: string;
  weight: number;
}

interface SpeechGrammarList {
  [index: number]: SpeechGrammar;
  length: number;
  addFromString(string: string, weight?: number): void;
  addFromURI(src: string, weight?: number): void;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  grammars: SpeechGrammarList;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onaudioend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onaudiostart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
  onnomatch: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onsoundend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onsoundstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onspeechend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onspeechstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  abort(): void;
  start(): void;
  stop(): void;
  addEventListener<K extends keyof SpeechRecognitionEventMap>(
    type: K,
    listener: (this: SpeechRecognition, ev: SpeechRecognitionEventMap[K]) => any,
    options?: boolean | AddEventListenerOptions
  ): void;
  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
  ): void;
  removeEventListener<K extends keyof SpeechRecognitionEventMap>(
    type: K,
    listener: (this: SpeechRecognition, ev: SpeechRecognitionEventMap[K]) => any,
    options?: boolean | EventListenerOptions
  ): void;
  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions
  ): void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition;
  prototype: SpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
    speechRecognitionEventsRegistered?: boolean;
    mocktrailWsConnections?: WebSocket[];
    altairTimers?: (NodeJS.Timeout | number)[];
    mocktrailMountCycles?: { component: string; mountTime: number }[];
    mocktrailAudioContexts?: AudioContext[];
    messageFinalizationTimer?: NodeJS.Timeout;
    userTranscriptionChunks?: string[];
    geminiTranscriptionChunks?: string[];
    currentElapsedTime?: number; // Store the current elapsed time from the timer
    interviewPageFullyStable?: boolean; // Flag to indicate when the interview page is fully stable
    lastProcessedTranscript?: string; // Store the last processed transcript
    isMicrophoneMuted?: boolean; // Add this new property
    isProcessingInterviewCompletion?: boolean; // Flag to block navigation during interview completion
  }
}

// Create a singleton speech recognition manager outside of React lifecycle
// This will persist even when components unmount and remount
const SpeechRecognitionManager = (() => {
  let instance: SpeechRecognition | null = null;
  let isActive = false;
  let isInitialized = false;
  let finalizeCallback: ((transcript: string) => void) | null = null;
  let currentTranscript = '';
  let transcriptionTimeout: NodeJS.Timeout | null = null;
  let userSpeakingTimeout: NodeJS.Timeout | null = null;
  let setUserSpeakingState: ((speaking: boolean) => void) | null = null;
  let setIsListeningState: ((listening: boolean) => void) | null = null;
  let userTurnCompleteCallback: ((transcript?: string) => void) | null = null;
  
  // Track timeouts to properly clean them up
  let finalizeTimeout: NodeJS.Timeout | null = null;
  let speechSilenceTimeout: NodeJS.Timeout | null = null;
  let restartTimeout: NodeJS.Timeout | null = null;
  let recognitionRestartCount = 0;
  const MAX_RESTART_ATTEMPTS = 3;
  let lastStartTime = 0;
  let lastStopTime = 0;
  
  // Flag to track if we're in an AI speech period
  let isAISpeaking = false;

  // Clear all timeouts
  const clearTimeouts = () => {
    if (transcriptionTimeout) {
      clearTimeout(transcriptionTimeout);
      transcriptionTimeout = null;
    }

    if (userSpeakingTimeout) {
      clearTimeout(userSpeakingTimeout);
      userSpeakingTimeout = null;
    }

    if (finalizeTimeout) {
      clearTimeout(finalizeTimeout);
      finalizeTimeout = null;
    }

    if (speechSilenceTimeout) {
      clearTimeout(speechSilenceTimeout);
      speechSilenceTimeout = null;
    }

    if (restartTimeout) {
      clearTimeout(restartTimeout);
      restartTimeout = null;
    }
  };

  // Fully clean up and re-initialize recognition
  const resetRecognition = () => {
    // Log detailed state
    console.log(`[SpeechRecognitionManager] Resetting recognition. Current state: isActive=${isActive}, instance=${instance ? 'exists' : 'null'}, recognitionRestartCount=${recognitionRestartCount}`);
    
    // Clean up current instance if it exists
    if (instance) {
      try {
        instance.abort();
      } catch (error) {
        console.error('[SpeechRecognitionManager] Error aborting recognition during reset:', error);
      }
      instance = null;
    }
    
    // Reset state flags
      isActive = false;
    if (setIsListeningState) {
      setIsListeningState(false);
    }
    
    // Clear all timeouts
    clearTimeouts();
    
    // Wait a moment before recreating
    setTimeout(() => {
      console.log('[SpeechRecognitionManager] Creating new recognition instance after reset');
      createRecognitionInstance();
      
      // Auto-start the new instance if not during AI speech
      if (!isAISpeaking) {
        setTimeout(() => {
          start();
        }, 300);
      } else {
        console.log('[SpeechRecognitionManager] AI is speaking, delaying restart of recognition');
      }
    }, 500);
  };

  // Helper to create a new recognition instance
  const createRecognitionInstance = () => {
    if (typeof window === 'undefined') return null;
    
    // Use the browser's SpeechRecognition API
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.error('[SpeechRecognitionManager] SpeechRecognition not supported in this browser');
      return null;
    }
    
    // Create a new instance
    instance = new SpeechRecognition();
    
    // Configure the instance with better settings for interview scenarios
    instance.continuous = true;
    instance.interimResults = true;
    instance.lang = 'en-US';
    
    // Adjust these parameters to improve voice detection and reduce interference
    // Lower the max alternatives to make recognition more focused
    instance.maxAlternatives = 3;
    
    // Enhanced audio constraints for better echo cancellation
    try {
      // @ts-ignore: This is a non-standard property but supported in some browsers
      instance.audioConstraints = {
        echoCancellation: true,
        sampleRate: 48000,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1
      };
    } catch (e) {
      console.log('[SpeechRecognitionManager] Browser doesn\'t support advanced audio constraints');
    }
    
    // Set up basic event handlers
    instance.onstart = () => {
      console.log('[SpeechRecognitionManager] Recognition started');
      isActive = true;
      if (setIsListeningState) {
        setIsListeningState(true);
      }
      lastStartTime = Date.now();
      recognitionRestartCount = 0;  // Reset counter on successful start
    };
    
    instance.onend = () => {
      console.log(`[SpeechRecognitionManager] Recognition ended, current transcript: ${currentTranscript}`);
      isActive = false;
      
      // If recognition ends too quickly, it might indicate a problem
      const runTime = Date.now() - lastStartTime;
      
      // Skip auto-restart if AI is speaking
      if (isAISpeaking) {
        console.log('[SpeechRecognitionManager] AI is speaking, not restarting recognition');
        if (setIsListeningState) {
        setIsListeningState(false);
        }
        return;
      }
      
      // Avoid trying to restart if we've been running less than 1 second
      // This helps prevent restart loops
      if (runTime < 800 && recognitionRestartCount < MAX_RESTART_ATTEMPTS) {
        recognitionRestartCount++;
        console.log(`[SpeechRecognitionManager] Recognition ended too quickly (${runTime}ms), attempt ${recognitionRestartCount}/${MAX_RESTART_ATTEMPTS} to restart`);
        
        // Wait longer with each successive failed attempt
        const delayTime = Math.min(1000 * (1 + recognitionRestartCount), 5000);
        
        // Try to restart with increasing delay
        restartTimeout = setTimeout(() => {
          console.log('[SpeechRecognitionManager] Auto-restarting recognition after early termination');
          
          // For repeated quick failures, completely reset the recognition instance
          if (recognitionRestartCount >= 2) {
            console.log('[SpeechRecognitionManager] Multiple quick failures, performing full reset before restart');
            resetRecognition();
          } else {
            start();
          }
        }, delayTime);
      } else if (recognitionRestartCount >= MAX_RESTART_ATTEMPTS) {
        console.warn('[SpeechRecognitionManager] Multiple restart attempts failed, performing full reset');
        
        // For persistent failures, perform a complete reset and pause before trying again
        resetRecognition();
        
        // Try one more time after a longer pause
        restartTimeout = setTimeout(() => {
          console.log('[SpeechRecognitionManager] Final attempt to restart after multiple failures');
          createRecognitionInstance();
          start();
        }, 5000);
      } else if (runTime >= 800) {
        // Normal end after reasonable run time - auto-restart if intended to be active
        if (setIsListeningState) {
          setIsListeningState(false);
        }
        
        // Auto-restart after a brief pause unless AI is speaking
        if (!isAISpeaking) {
          // Use a random delay between 500-1000ms to avoid potential timing issues
          const randomDelay = 500 + Math.floor(Math.random() * 500);
          restartTimeout = setTimeout(() => {
            start();
          }, randomDelay);
        }
      }
    };
    
    instance.onerror = (event) => {
      console.error(`[SpeechRecognitionManager] Error: ${event.error}, ${event.message}`);
      
      // Handle no-speech error specifically
      if (event.error === 'no-speech') {
        console.log('[SpeechRecognitionManager] No speech detected, attempting recovery');
        resetRecognition();
      }
      
      // For other errors, we'll let onend handle it
    };
    
    // Handle speech detection events
      instance.onspeechstart = () => {
        console.log('[SpeechRecognitionManager] Speech detected');
        if (setUserSpeakingState) {
          setUserSpeakingState(true);
        }
      };
      
      instance.onspeechend = () => {
        console.log('[SpeechRecognitionManager] Speech ended');
      // Don't set speaking state to false immediately to avoid flickering
      // We'll handle this with a timeout
    };
    
    instance.onaudiostart = () => {
      console.log('[SpeechRecognitionManager] Audio capturing started');
      };
      
      instance.onaudioend = () => {
        console.log('[SpeechRecognitionManager] Audio capturing ended');
      };
      
    return instance;
  };

  // Clean up resources
  const cleanup = () => {
    console.log('[SpeechRecognitionManager] Performing complete cleanup of speech recognition');
    
    // Stop recognition if active
    if (instance) {
      try {
        console.log('[SpeechRecognitionManager] Aborting active speech recognition instance');
        instance.onresult = null;
        instance.onstart = null;
        instance.onend = null;
        instance.onerror = null;
        instance.onspeechstart = null;
        instance.onspeechend = null;
        instance.onaudiostart = null;
        instance.onaudioend = null;
        instance.abort();
      } catch (error) {
        console.error('[SpeechRecognitionManager] Error aborting recognition during cleanup:', error);
      }
      instance = null;
    }
    
    // Clear all timeouts
    clearTimeouts();
    
    // Reset state completely
        isActive = false;
    isInitialized = false;
    finalizeCallback = null;
    currentTranscript = '';
    setUserSpeakingState = null;
    setIsListeningState = null;
    userTurnCompleteCallback = null;
    recognitionRestartCount = 0;
    isAISpeaking = false;
    
    console.log('[SpeechRecognitionManager] Speech recognition cleanup completed');
  };

  // Initialize speech recognition
  const initialize = (
    finalize: (transcript: string) => void,
    setUserSpeaking: (speaking: boolean) => void,
    setIsListening: (listening: boolean) => void,
    onUserTurnComplete: ((transcript?: string) => void) | null = null,  // Fix: Make it null by default instead of undefined
    isMicMuted: boolean = false  // Add this parameter
  ) => {
    // Store callbacks
    finalizeCallback = finalize;
    setUserSpeakingState = setUserSpeaking;
    setIsListeningState = setIsListening;
    userTurnCompleteCallback = onUserTurnComplete;
    
    // Store the mute state in global window object for cross-component access
    window.isMicrophoneMuted = isMicMuted;
    
    // Skip if already initialized
    if (isInitialized) {
      // console.log('[SpeechRecognitionManager] Already initialized, skipping initialization');
      return;
    }

    isInitialized = true;
    
    // Create the recognition instance
    createRecognitionInstance();
    
    if (!instance) {
      console.error('[SpeechRecognitionManager] Failed to initialize speech recognition');
      return;
    }

    // Set up the main results handler
      instance.onresult = (event) => {
        try {
        // Check if recognition is active and not muted
        if (!isActive || isAISpeaking) {
          return;
        }
        
        // CRITICAL FIX: First check if we're actually muted at the app level - skip all processing if so
        if (window.isMicrophoneMuted === true) {
          console.log('[SpeechRecognitionManager] Microphone is muted, ignoring speech recognition results');
          return;
        }
        
        // Process the newest results
        const results = event.results;
        const resultIndex = event.resultIndex;
        const finalTranscripts: string[] = [];
        const interimTranscripts: string[] = [];
        let shouldProcessTranscript = false;
        let cleanedTranscript = '';
        
        // Loop through all results since the last result
        for (let i = resultIndex; i < results.length; i++) {
          // Choose the first alternative (most confident)
          const transcript = results[i][0].transcript;
          
          // Skip empty transcripts
          if (!transcript.trim()) {
            continue;
          }
          
          // Clean up the transcript to remove unnecessary spacing
          const trimmedTranscript = transcript.trim();
          
          // Add to the appropriate collection
          if (results[i].isFinal) {
            finalTranscripts.push(trimmedTranscript);
            shouldProcessTranscript = true;
            console.log(`[SpeechRecognitionManager] Got final transcript: "${trimmedTranscript}"`);
            
            // Important: For final results, trigger immediate finalization
            if (trimmedTranscript.length > 0 && userTurnCompleteCallback) {
              console.log(`[SpeechRecognitionManager] isFinal=true, finalizing immediately: "${trimmedTranscript}"`);
              // Update current transcript
              currentTranscript = trimmedTranscript;
              
              // Call finalize to ensure the transcript is processed properly
              if (finalizeCallback) {
                finalizeCallback(trimmedTranscript);
              }
              
              // Call turn complete with a small delay to allow UI update
              setTimeout(() => {
                if (userTurnCompleteCallback) {
                  userTurnCompleteCallback(trimmedTranscript);
                }
              }, 100);
            }
          } else {
            interimTranscripts.push(trimmedTranscript);
            
            // IMPORTANT: Force-process high-confidence results as final
            // This helps when the browser doesn't mark speech as final
            if (results[i][0].confidence > 0.4) {
              console.log(`[SpeechRecognitionManager] Force-processing high-confidence (${results[i][0].confidence.toFixed(2)}) result as final`);
              finalTranscripts.push(trimmedTranscript);
              shouldProcessTranscript = true;
              
              // For very high confidence (0.7+), trigger immediate finalization
              if (results[i][0].confidence > 0.7 && userTurnCompleteCallback) {
                // Look for sentence ending punctuation or at least 4 words or very high confidence
                const endsWithPunctuation = /[.!?]$/.test(trimmedTranscript);
                const wordCount = trimmedTranscript.split(/\s+/).length;
                
                // CRITICAL FIX: Make this threshold lower to finalize more aggressively
                // We'd rather have slightly fragmented transcripts than no transcripts
                const likelyComplete = endsWithPunctuation || wordCount >= 1 || 
                                     results[i][0].confidence > 0.75;
                
                if (likelyComplete) {
                  console.log(`[SpeechRecognitionManager] High confidence (${results[i][0].confidence.toFixed(2)}) detected, finalizing immediately`);
                  
                  // Update transcript first to ensure the callback uses the full text
                  currentTranscript = trimmedTranscript;
                  
                  // Call finalize to ensure the transcript is processed properly
                  if (finalizeCallback) {
                    finalizeCallback(trimmedTranscript);
                  }
                  
                  // CRITICAL FIX: Don't delay the callback, call immediately to ensure messages appear
                  if (userTurnCompleteCallback) {
                    userTurnCompleteCallback(trimmedTranscript);
                    
                    // Clear the transcript after finalizing
                    currentTranscript = '';
                  }
                } else {
                  console.log(`[SpeechRecognitionManager] High confidence but not finalizing yet: "${trimmedTranscript}"`);
                }
              }
            }
          }
          
          // Use the latest result for confidence checking
          if (i === results.length - 1) {
            cleanedTranscript = trimmedTranscript;
          }
        }
        
        // Get the first result for confidence checking
        if (results[resultIndex]) {
          const confidence = results[resultIndex][0].confidence;
          const isFinal = results[resultIndex].isFinal;
          
          // Log with more information about the speech detection
          console.log(`[SpeechRecognitionManager] Speech detected: "${cleanedTranscript}" - confidence: ${confidence.toFixed(2)}, isFinal: ${isFinal}`);
          
          // Special handling for first words - accept with minimal confidence threshold
          if (!currentTranscript && cleanedTranscript) {
            console.log(`[SpeechRecognitionManager] First words detected: "${cleanedTranscript}"`);
            shouldProcessTranscript = true;
            // Set user speaking state immediately on first words
            if (setUserSpeakingState) {
              setUserSpeakingState(true);
            }
          } 
          // For ongoing speech, use a lower threshold to improve detection reliability
          else if (cleanedTranscript && (confidence > 0.01 || currentTranscript)) {
            shouldProcessTranscript = true;
          }
          // Always process final results
          else if (isFinal && cleanedTranscript) {
            shouldProcessTranscript = true;
          }
        }
        
        // Process transcripts if we have something to work with
        if (shouldProcessTranscript) {
          // Combine final transcripts or use the current interim if no finals
          const combinedTranscript = finalTranscripts.length > 0 
            ? finalTranscripts.join(' ') 
            : interimTranscripts.join(' ');
          
          // Avoid empty transcripts
          if (combinedTranscript.trim()) {
            // Update current transcript
            currentTranscript = combinedTranscript;
            
            // Call the callback function with the current transcript
            if (finalizeCallback) {
              finalizeCallback(currentTranscript);
            }
            
            // Set user speaking state to true
            if (setUserSpeakingState) {
              setUserSpeakingState(true);
            }
            
            // Clear the previous speech silence timeout
            if (speechSilenceTimeout) {
              clearTimeout(speechSilenceTimeout);
              speechSilenceTimeout = null;
            }
            
            // Set a timeout to consider the user has stopped speaking
            speechSilenceTimeout = setTimeout(() => {
              console.log('[SpeechRecognitionManager] Speech silence timeout - user appears to have stopped speaking');
              
              // Only finalize if we have a non-empty current transcript
              if (currentTranscript.trim()) {
                // Check if this looks like a mid-sentence pause rather than end of speech
                const endsWithConjunction = /\b(and|but|or|so|because|if|when|while|as|that|than|with|for|to|of|in|on|at|by|about)\s*$/i.test(currentTranscript.trim());
                const noEndPunctuation = !/[.!?]$/.test(currentTranscript.trim());
                const shortPhraseInProgress = currentTranscript.trim().split(/\s+/).length < 3;
                const likelyContinuingSpeech = (endsWithConjunction || (noEndPunctuation && shortPhraseInProgress)) && 
                                           currentTranscript.length > 5;
                
                // If it seems the user is mid-thought, wait longer before finalizing
                if (likelyContinuingSpeech) {
                  console.log('[SpeechRecognitionManager] Mid-sentence pause detected, waiting for continued speech');
                  return; // Don't finalize yet, user is likely still forming thoughts
                }
                
                if (finalizeCallback) {
                  finalizeCallback(currentTranscript);
                }
                
                // CRITICAL FIX: Always call user turn complete callback to ensure messages appear
                // and make this immediate rather than delayed
                if (userTurnCompleteCallback) {
                  console.log('[SpeechRecognitionManager] User turn complete - calling callback');
                  userTurnCompleteCallback(currentTranscript);
                  
                  // Clear the transcript after finalizing to avoid duplicate processing
                  currentTranscript = '';
                }
              }
            }, 700); // CRITICAL FIX: Increased to 700ms to allow for natural thinking pauses while maintaining good responsiveness
            }
        }
      } catch (error) {
        console.error('[SpeechRecognitionManager] Error processing recognition results:', error);
      }
    };
  };

  // Start speech recognition
  const start = () => {
    // Skip start if AI is speaking
    if (isAISpeaking) {
      console.log('[SpeechRecognitionManager] AI is speaking, not starting recognition');
      return;
    }
    
    // Check if we're already active according to our state tracking 
    if (isActive) {
      console.log('[SpeechRecognitionManager] Recognition already active according to our state');
      return;
    }
    
    // Make sure we have an instance
    if (!instance) {
      createRecognitionInstance();
    }
    
    try {
      // Before starting, check browser state to avoid InvalidStateError
      // We'll try to abort first to guarantee a clean state
      try {
        if (instance) {
          instance.abort();
          console.log('[SpeechRecognitionManager] Aborted existing instance before starting');
        }
      } catch (abortError) {
        // Just log and continue - this error is non-critical
        console.log('[SpeechRecognitionManager] Error aborting before start (non-critical):', abortError);
      }
      
      // Short delay to let browser reset state
      setTimeout(() => {
        if (instance) {
          console.log('[SpeechRecognitionManager] Starting recognition');
          try {
            instance.start();
            console.log('[SpeechRecognitionManager] Recognition start called successfully');
            isActive = true;
          } catch (startError) {
            // If we get InvalidStateError, recognition is already active
            if (startError instanceof DOMException && startError.name === 'InvalidStateError') {
              console.log('[SpeechRecognitionManager] Recognition is already active (InvalidStateError)');
              isActive = true;
            } else {
              // Handle other errors
              console.error('[SpeechRecognitionManager] Error starting recognition:', startError);
              instance = null;
              isActive = false;
              
              // Try to reset and recover
              resetRecognition();
            }
          }
        } else {
          console.error('[SpeechRecognitionManager] Failed to start - no instance available');
        }
      }, 50);  // 50ms delay to let the browser reset
    } catch (error) {
      console.error('[SpeechRecognitionManager] Error starting recognition:', error);
      
      // Handle InvalidStateError specifically - this means the browser thinks recognition is already running
      if (error instanceof DOMException && error.name === 'InvalidStateError') {
        console.log('[SpeechRecognitionManager] Browser reports recognition already active, updating state');
        isActive = true;
        return; // No need to try recovery - we're already active
      }
      
      instance = null;
      isActive = false;
      
      // Try to recover by resetting completely
      setTimeout(() => {
        resetRecognition();
      }, 500);
      
      if (setIsListeningState) {
        setIsListeningState(false);
      }
    }
  };

  // Stop speech recognition
  const stop = () => {
    // Exit early if we've recently stopped (to prevent duplicate errors)
    const now = Date.now();
    if (lastStopTime && now - lastStopTime < 100) {
      console.log(`[SpeechRecognitionManager] Ignoring stop request - too soon since last stop (${now - lastStopTime}ms)`);
      return;
    }
    
    // Update stop time
    lastStopTime = now;
    
    console.log(`[SpeechRecognitionManager] Stopping recognition. Active=${isActive}, HasTranscript=${!!currentTranscript}, TranscriptLength=${currentTranscript ? currentTranscript.length : 0}`);
    
    if (isActive) {
      console.log('[SpeechRecognitionManager] Stopping active recognition');
      isActive = false;
      
      // CRITICAL FIX: Make sure to finalize any transcript before stopping
      if (currentTranscript && currentTranscript.trim()) {
        console.log(`[SpeechRecognitionManager] Preserving transcript during stop: "${currentTranscript}"`);
        
        // Call finalize callback
        if (finalizeCallback) {
          finalizeCallback(currentTranscript);
        }
        
        // Schedule processing of the preserved transcript
        setTimeout(() => {
          console.log('[SpeechRecognitionManager] Processing preserved transcript after stop');
          if (userTurnCompleteCallback && currentTranscript) {
            userTurnCompleteCallback(currentTranscript);
            // Clear after processing
            currentTranscript = '';
          }
        }, 100);
      }
      
      try {
        if (instance) {
          instance.abort();
        }
      } catch (error) {
        console.error('[SpeechRecognitionManager] Error stopping recognition', error);
      }
    }
  };

  // Register the global event handlers for page visibility and unload
  const registerGlobalEvents = () => {
    // Prevent registering more than once
    if (window.speechRecognitionEventsRegistered) {
      return;
    }
    
    window.speechRecognitionEventsRegistered = true;
    
    const handleBeforeUnload = () => {
      console.log('[SpeechRecognitionManager] Page unload detected, stopping recognition');
      cleanup();
    };
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('[SpeechRecognitionManager] Page hidden, pausing recognition');
        
        // Finalize current transcript if needed
        if (currentTranscript.trim() && isActive && finalizeCallback) {
          finalizeCallback(currentTranscript);
          currentTranscript = '';
        }
        
        // Just stop, don't clean up
        if (instance) {
          try {
            instance.stop();
          } catch (err) {
            console.warn('[SpeechRecognitionManager] Error stopping recognition on page hide:', err);
          }
        }
      } else {
        console.log('[SpeechRecognitionManager] Page visible again, resuming recognition if needed');
        
        // Only restart if we're still supposed to be active
        if (isActive && instance) {
          try {
            instance.start();
          } catch (err) {
            console.warn('[SpeechRecognitionManager] Error resuming recognition on page show:', err);
          }
        }
      }
    };
    
    // Register the event handlers only once
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    console.log('[SpeechRecognitionManager] Global event handlers registered');
  };

  // Method to set AI speaking state - for preventing pickup of AI audio
  const setAISpeaking = (speaking: boolean) => {
    console.log(`[SpeechRecognitionManager] AI speaking state changed to: ${speaking}`);
    const wasAISpeaking = isAISpeaking;
    isAISpeaking = speaking;
    
    // If AI starts speaking, stop recognition to prevent feedback
    if (speaking && isActive) {
      console.log('[SpeechRecognitionManager] AI started speaking, pausing recognition');
      stop();
    } 
    // If AI stops speaking, restart recognition after a delay to ensure audio has stopped
    else if (!speaking && wasAISpeaking && isInitialized) {
      console.log('[SpeechRecognitionManager] AI stopped speaking, restarting recognition after delay');
      
      // Clear the current transcript to prevent old text from being reprocessed
      currentTranscript = '';
      
      // Restart after a short delay to allow audio to fully stop
      restartTimeout = setTimeout(() => {
        // Only restart if we're still supposed to be active and initialized
        if (isInitialized) {
          try {
            start();
          } catch (err) {
            console.error('[SpeechRecognitionManager] Error restarting recognition after AI spoke:', err);
            
            // If we failed to restart, try one more time with a reset
            setTimeout(() => {
              resetRecognition();
            }, 300);
          }
        }
      }, 300);
    }
  };

  // Add a setMuted function to the SpeechRecognitionManager
  const setMuted = (muted: boolean) => {
    console.log('[SpeechRecognitionManager] Setting mute state to:', muted);
    
    // CRITICAL FIX: Always update the global property to ensure it's accessible everywhere
    window.isMicrophoneMuted = muted;
    
    // When muting, stop any speech recognition that's active
    if (muted && isActive) {
      // If muting while active, try to preserve any transcript
      stop();
    }
    
    // When unmuting, restart recognition if AI isn't speaking
    if (!muted && !isAISpeaking && !isActive) {
      // Create a short delay to allow browser state to reset
      setTimeout(() => {
        start();
      }, 300);
    }
  };

  // Return the public methods
  return {
    initialize,
    start,
    stop,
    cleanup,
    registerGlobalEvents,
    getStatus: () => ({ isActive, isInitialized }),
    getCurrentTranscript: () => currentTranscript,
    setAISpeaking, // New method to control recognition during AI speech
    setMuted // New method to control mute state
  };
})();

// Define the extended client type
interface ExtendedMultimodalLiveClient extends Omit<MultimodalLiveClient, 'sendText'> {
  // Use any for the input type to avoid compatibility issues
  sendRealtimeInput(chunks: GenerativeContentBlob[]): void;
  // Make sendText optional to accommodate the different signature
  sendText?: (text: string, turnComplete?: boolean) => void;
}

// Props interface for the provider
interface InterviewProviderProps {
  children: React.ReactNode;
  interviewId: string;
  interviewDetails?: InterviewDetails;
  videoRef: React.RefObject<HTMLVideoElement>;
  videoStream: MediaStream | null;
  onRestartInterview?: () => void;
  onInterviewComplete?: (interviewId: string, seriesId: string | undefined, feedbackApplicable: boolean, feedbackData?: InterviewFeedback | null) => Promise<void>;
  onVideoStreamChange?: (stream: MediaStream | null) => void;
  speechRecognitionEnabled?: boolean; // Add boolean prop to control SpeechRecognitionManager
  userData?: {
    name?: string;
    profileImage?: string;
    interviewGoals?: string[] | null;
    minutesLeft?: number;
  };
}

// Context interface
interface InterviewContextType {
  // Interview details
  interviewDetails: InterviewDetails;
  SetInterviewDetails: (details: InterviewDetails) => void;
  userInterviewId: string;
  SetUserInterviewId: (id: string) => void;
  userData: {
    name?: string;
    profileImage?: string;
    minutesLeft?: number;
  };
  // Messages and transcript
  messages: Message[]
  sendUserMessage: (text: string) => void;
  downloadTranscript: () => void;
  
  isExitingInterview: boolean;
  // Timer related
  elapsedTime: number;
  // totalDuration: number;
  formatTime: (seconds: number) => string;
  timerCompleted: boolean;
  setTimerCompleted: (completed: boolean) => void;
  subscriptionMinutesConsumed: boolean; // Add this new property
  
  // Video/audio states
  isCameraEnabled: boolean;
  isMicEnabled: boolean;
  isScreenSharing: boolean;
  toggleCamera: () => void;
  toggleMicrophone: () => void;
  toggleScreenShare: () => void;
  updateVideoStream: (stream: MediaStream | null) => void;
  
  // Device management
  videoDevices: Array<{ deviceId: string; label: string }>;
  audioDevices: Array<{ deviceId: string; label: string }>;
  selectedVideoDevice: string;
  selectedAudioDevice: string;
  handleDeviceChange: (deviceId: string, type: 'video' | 'audio') => void;
  
  // UI states
  showAiAvatar: boolean;
  toggleFocus: () => void;
  isDeviceDropdownOpen: boolean;
  toggleDeviceDropdown: () => void;
  showMessageInput: boolean;
  toggleMessageInput: () => void;
  isMicMuted: boolean;
  audioLevel: number;
  isSpeaking: boolean;
  
  // Interview control
  handleEndInterview: () => void;
  handleRestartInterview: () => void;
  showEndInterviewConfirmation: boolean;
  setShowEndInterviewConfirmation: (show: boolean) => void;
  showRestartConfirmation: boolean;
  setShowRestartConfirmation: (show: boolean) => void;
  confirmRestartInterview: () => void;
  confirmEndInterview: (finalElapsedTime: number) => Promise<void>;
  
  // Screen sharing control
  showStopScreenShareConfirmation: boolean;
  setShowStopScreenShareConfirmation: (show: boolean) => void;
  stopScreenSharing: () => Promise<void>;
  
  // Connection status
  connected: boolean;
  connectionHealthy: boolean;
  extendedClient: ExtendedMultimodalLiveClient | null;
  
  // Error handling
  cameraError: boolean;
  connectionErrorVisible: boolean;
  connectionErrorType: 'quota_exceeded' | 'connection_error' | 'general_error';
  setConnectionErrorVisible: (visible: boolean) => void;
  setConnectionErrorType: (type: 'quota_exceeded' | 'connection_error' | 'general_error') => void;
  
  // Feedback generation
  isGeneratingFeedback: boolean;
  isInterviewerThinking: boolean;
  setIsInterviewerThinking: (thinking: boolean) => void;
  
  // Refs
  videoRef: React.RefObject<HTMLVideoElement>;
  videoStream: MediaStream | null;
  userSpeakingRef: React.RefObject<boolean>;
  aiSpeakingRef: React.RefObject<boolean>;
  currentTranscriptRef: React.MutableRefObject<string>;
  
  // Misc utilities
  getCameraStatusMessage: () => string;
  
  // Speech recognition state
  isListening: boolean;
  userSpeaking: boolean;
  // Speech recognition and transcription
  modelTranscript: string;
  
  // Add the toggle function
  toggleTranscriptionType: () => void;
  
}

// Create the context
const InterviewContext = createContext<InterviewContextType | undefined>(undefined);

// Create a singleton AudioContext for output, prefer 24000Hz if possible
let outputAudioContext: AudioContext | null = null;
function getOutputAudioContext() {
  if (!outputAudioContext) {
    try {
      outputAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
      if (outputAudioContext.sampleRate !== 24000) {
        console.warn('[InterviewContext] Browser did not honor 24000Hz, got', outputAudioContext.sampleRate);
      }
    } catch (e) {
      // fallback to default
      outputAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      console.warn('[InterviewContext] Fallback to default AudioContext sample rate:', outputAudioContext.sampleRate);
    }
  }
  return outputAudioContext;
}

// Create a singleton AudioStreamer at the module level
let audioStreamer: AudioStreamer | null = null;
export function getAudioStreamer() {
  if (!audioStreamer) {
    const ctx = getOutputAudioContext();
    audioStreamer = new AudioStreamer(ctx);
    // Set the source sample rate for resampling if needed
    // audioStreamer.sourceSampleRate = 24000; // Adjust if your server uses a different rate
  }
  return audioStreamer;
}

// Provider component
export const InterviewProvider: React.FC<InterviewProviderProps> = ({ 
  children, 
  interviewId,
  interviewDetails: initialInterviewDetails = {},
  videoRef,
  videoStream,
  onInterviewComplete,
  onRestartInterview,
  onVideoStreamChange,
  speechRecognitionEnabled = true, // Add default value
  userData = { name: 'User', profileImage: '', minutesLeft: 0, interviewGoals: [] }
}) => {
  // Get API client from context
  const liveAPI = useLiveAPIContext();
  const connectFn = useRef(liveAPI.connect).current;
  
  // Create a unique ID for this provider instance for tracking
  const providerId = useRef(`interview-provider-${Date.now()}-${Math.floor(Math.random() * 10000)}`).current;
  
  // Track if WebSocket was connected by this instance
  const wasConnectedRef = useRef(false);
  
  // Track if component is mounted
  const isMountedRef = useRef(true);
  
  // Add a ref to track the last time we added a transcript to messages
  // This helps avoid duplicate messages from different transcription sources
  const lastTranscriptTimeRef = useRef<number>(0);
  const lastTranscriptTextRef = useRef<string>('');
  
  
  // Initialize state for provider mounted
  useEffect(() => {
    console.log(`[InterviewProvider] [${providerId}] Provider mounted`);
    isMountedRef.current = true;
    
    return () => {
      console.log(`[InterviewProvider] [${providerId}] Provider unmounting, cleaning up resources`);
      isMountedRef.current = false;
      
      // Ensure WebSocket is disconnected if this instance connected it
      if (wasConnectedRef.current && liveAPI.disconnect) {
        console.log(`[InterviewProvider] [${providerId}] Disconnecting WebSocket that was connected by this instance`);
        liveAPI.disconnect().catch(err => {
          console.error(`[InterviewProvider] [${providerId}] Error disconnecting:`, err);
        });
      }
    };
  }, [liveAPI.disconnect]);
  
  // State for interview details
  const [interviewDetails, setInterviewDetails] = useState<InterviewDetails>(initialInterviewDetails);
  
  // State for user interview id
  const [userInterviewId, setUserInterviewId] = useState<string>('');
  // Messages and transcript state
  const [messages, setMessages] = useState<Message[]>([]);
  const lastRenderedMessageIdRef = useRef<string>('');
  
  // Initialize the state for speech recognition
  const [isListening, setIsListening] = useState<boolean>(true);
  const [userSpeaking, setUserSpeaking] = useState<boolean>(false);
  const currentTranscriptRef = useRef<string>('');
  
  // Model transcription state (from Gemini)
  const [modelTranscript, setModelTranscript] = useState<string>('');

  // Timer state
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const elapsedTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [timerCompleted, setTimerCompleted] = useState<boolean>(false);
  
  // Device state
  const storedMicPreference = typeof window !== 'undefined'
    ? sessionStorage.getItem('hasMicPermission')
    : null;

  const initialMicEnabled = storedMicPreference === null ? true : storedMicPreference === 'true';

  const [isCameraEnabled, setIsCameraEnabled] = useState<boolean>(
    sessionStorage.getItem('hasCameraPermission') === 'true'
  );
  const [isMicEnabled, setIsMicEnabled] = useState<boolean>(initialMicEnabled);

  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>(
    sessionStorage.getItem('selectedVideoDevice') || ''
  );
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>(
    sessionStorage.getItem('selectedAudioDevice') || ''
  );
  
  const [isMicMuted, setIsMicMuted] = useState<boolean>(!initialMicEnabled);
  const [isScreenSharing, setIsScreenSharing] = useState<boolean>(false);
  const [videoDevices, setVideoDevices] = useState<Array<{ deviceId: string; label: string }>>([]);
  const [audioDevices, setAudioDevices] = useState<Array<{ deviceId: string; label: string }>>([]);
  const [cameraError, setCameraError] = useState<boolean>(false);
  
  // UI state
  const [showAiAvatar, setShowAiAvatar] = useState<boolean>(true);
  const [isDeviceDropdownOpen, setIsDeviceDropdownOpen] = useState<boolean>(false);
  const [showMessageInput, setShowMessageInput] = useState<boolean>(false);
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [connectionHealthy, setConnectionHealthy] = useState<boolean>(true);
  
  // Modal state
  const [showEndInterviewConfirmation, setShowEndInterviewConfirmation] = useState<boolean>(false);
  const [showRestartConfirmation, setShowRestartConfirmation] = useState<boolean>(false);
  const [showStopScreenShareConfirmation, setShowStopScreenShareConfirmation] = useState<boolean>(false);
  
  // Feedback generation state
  const [isGeneratingFeedback, setIsGeneratingFeedback] = useState<boolean>(false);
  const [isInterviewerThinking, setIsInterviewerThinking] = useState<boolean>(false);
  
  const [isExitingInterview, setIsExitingInterview] = useState<boolean>(false);
  // Connection error state
  const [connectionErrorVisible, setConnectionErrorVisible] = useState<boolean>(false);
  const [connectionErrorType, setConnectionErrorType] = useState<'quota_exceeded' | 'connection_error' | 'general_error'>('general_error');
  
  // Refs for tracking speaking state - these are still needed by other parts of the code
  const userSpeakingRef = useRef<boolean>(false);
  const aiSpeakingRef = useRef<boolean>(false);
  
  // Add this to the state definitions
  const [subscriptionMinutesConsumed, setSubscriptionMinutesConsumed] = useState<boolean>(false);

  // Refactored handleAudio to use AudioStreamer singleton
  const handleAudio = useCallback(async (audioData: any) => {
    try {
      if (!audioData || !(audioData instanceof ArrayBuffer) || audioData.byteLength === 0) {
        console.warn('[InterviewContext] Invalid audio data received, skipping playback');
        return;
      }
      const streamer = getAudioStreamer();
      streamer.addPCM16(new Uint8Array(audioData));
    } catch (error) {
      console.error('[InterviewContext] Error in handleAudio, suppressing to prevent remounts:', error);
    }
  }, []);
  
  // Initialize the elapsed time timer
  useEffect(() => {
    
    
    // console.log('[InterviewContext] Initializing timer with current elapsed time:', elapsedTime);
    
    // Timer for tracking elapsed time - start immediately without waiting for page stability
    const timerInterval = setInterval(() => {
      setElapsedTime(prev => {
        const nextValue = prev + 1;
        
        // Check for subscription minutes limit
        if (userData?.minutesLeft !== undefined) {
          // Convert elapsed seconds to minutes (rounded up to be lower bound) example 64s -> 1m not 2m
          const elapsedMinutes = Math.floor(nextValue / 60);
          const minutesLeft = userData.minutesLeft;
          
          // Check if user has consumed all available minutes
          if (elapsedMinutes >= minutesLeft && !subscriptionMinutesConsumed) {
            console.log(`[InterviewContext] Subscription minutes consumed: ${elapsedMinutes}/${minutesLeft}`);
            
            // Set the subscription minutes consumed flag
            setSubscriptionMinutesConsumed(true);
            
            // Stop the timer
            if (elapsedTimerRef.current) {
              console.log('[InterviewContext] Stopping timer due to subscription minutes consumed');
              clearInterval(elapsedTimerRef.current);
              elapsedTimerRef.current = null;
            }
            
            // Disconnect WebSocket and perform cleanup
            if (liveAPI.client && liveAPI.connected) {
              console.log('[InterviewContext] Disconnecting WebSocket due to subscription minutes consumed');
              // Use a clean disconnection without triggering reconnection attempts
              try {
                // Set connection as healthy to prevent reconnection indicators
                setConnectionHealthy(true);
                
                // Stop speech recognition to prevent further transcriptions
                if (speechRecognitionEnabled) {
                SpeechRecognitionManager.stop();
                }
                
                // Disconnect the WebSocket
                liveAPI.disconnect().catch(err => {
                  console.error('[InterviewContext] Error disconnecting WebSocket after minutes consumed:', err);
                });
              } catch (error) {
                console.error('[InterviewContext] Error during WebSocket cleanup after minutes consumed:', error);
              }
            }
          }
        }
        
        // Check if timer has reached the end
        // if (nextValue >= totalDuration && !timerCompleted) {
          // setTimerCompleted(true);
        // }
 
        return nextValue;
      });
    }, 1000);
    
    // Store timer reference for cleanup
    elapsedTimerRef.current = timerInterval;
    
    // console.log('[InterviewContext] Timer initialized with interval ID:', timerInterval);
    
    return () => {
      if (timerInterval) {
        // console.log('[InterviewContext] Cleaning up timer interval:', timerInterval);
        clearInterval(timerInterval);
        elapsedTimerRef.current = null;
      }
      
    };
  }, [timerCompleted, userData?.minutesLeft, subscriptionMinutesConsumed, liveAPI, interviewDetails, elapsedTime]);
  
  // Format time helper function
  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);
  
  // Function to finalize the current transcript and add it as a message
  const finalizeCurrentTranscript = useCallback(() => {
    const currentTranscript = currentTranscriptRef.current.trim();

    if (currentTranscript && currentTranscript.length > 0) {
      console.log('[Transcription] Finalizing transcript and adding to UI:', currentTranscript);
      
      // Don't add duplicate messages 
      const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
      if (lastMessage && lastMessage.sender === 'user' && lastMessage.text === currentTranscript) {
        console.log('[Transcription] Skipping duplicate user message:', currentTranscript);
        return;
      }
      
      // Add to messages with user as sender
      // Commenting out for now.
      // setMessages(prev => [...prev, {
      //   id: uuidv4(),
      //   text: currentTranscript,
      //   sender: 'user',
      //   timestamp: new Date().toISOString()
      // }]);
      
      // Clear the transcript after adding it as a message
      currentTranscriptRef.current = '';
    }
  }, [messages, setMessages]);

  // Create a helper function to handle user turn completion
      const handleUserTurnComplete = useCallback((forcedTranscript?: string) => {
      console.log('[InterviewContext] User turn complete - finalizing user transcription');
    
    // Use the provided transcript if available, otherwise use the current transcript
    const transcriptToProcess = forcedTranscript || currentTranscriptRef.current || '';
    
    // When the user's turn is complete, ensure any final transcription gets added to messages
    if (transcriptToProcess) {
      // Ensure we're working with properly trimmed text
      const trimmedText = transcriptToProcess.trim();
      
      console.log('[InterviewContext] Processing browser transcript for messages:', trimmedText);
      
      // Skip processing if microphone is muted
      if (isMicMuted) {
        console.log('[InterviewContext] Microphone is muted, not adding transcript to messages');
        return;
      }
      
      // CRITICAL FIX: We should almost never skip adding a message during user turn complete
      // as this is a finalization event. Only check for EXACT duplicates with a very short window (300ms)
      const now = Date.now();
      const isRecentExactDuplicate = now - lastTranscriptTimeRef.current < 300 && 
                                      lastTranscriptTextRef.current === trimmedText;
      
      if (isRecentExactDuplicate) {
        console.log(`[InterviewContext] Skipping exact duplicate transcript: "${trimmedText}" (${now - lastTranscriptTimeRef.current}ms ago)`);
        return;
      }
      
      // Only proceed if we have actual content
      if (trimmedText) {
        console.log('[InterviewContext] Adding final browser transcription to messages:', trimmedText);
        
        // IMPORTANT: Capture the transcript FIRST to prevent race conditions
        lastTranscriptTextRef.current = trimmedText;
        lastTranscriptTimeRef.current = now;
        // Immediately add the message without additional checks
        // Commenting out for now.
        // setMessages(prev => {
        //   // Create a basic duplicate check for messages added in the last 2 seconds
        //   const recentMessages = prev.slice(-3);
        //   const lastMessage = recentMessages.length > 0 ? recentMessages[recentMessages.length - 1] : null;
          
        //   // If last message is identical and very recent (within 2 seconds), skip
        //   if (lastMessage && 
        //       lastMessage.sender === 'user' && 
        //       lastMessage.text === trimmedText &&
        //       (now - new Date(lastMessage.timestamp).getTime() < 2000)) {
        //     return prev; // Skip exact duplicate
        //   }
          
        //   // If last message is from user and this new text contains it completely
        //   // update that message instead of adding a new one
        //   if (lastMessage && 
        //       lastMessage.sender === 'user' && 
        //       trimmedText.includes(lastMessage.text) &&
        //       (now - new Date(lastMessage.timestamp).getTime() < 3000)) {
        //     return prev.map((msg, index) => 
        //       index === prev.length - 1 
        //         ? { ...msg, text: trimmedText, timestamp: new Date().toISOString() }
        //         : msg
        //     );
        //   }
          
        //   // Add as a new message in all other cases
        //   return [...prev, {
        //     id: uuidv4(),
        //     text: trimmedText,
        //     sender: 'user',
        //     timestamp: new Date().toISOString()
        //   }];
        // });
        
        // Clear the transcript after adding it as a message
        currentTranscriptRef.current = '';
      }
    }
  }, [setMessages, currentTranscriptRef, isMicMuted]);
  
  // Replace initializeSpeechRecognition with a function that checks the active transcription type
  const initializeSpeechRecognition = useCallback(() => {
    if (!speechRecognitionEnabled) {
      console.log('[InterviewContext] Speech recognition disabled, skipping initialization');
      return null;
    }
    
    console.log('[InterviewContext] Using hybrid transcription approach (Browser UI + Gemini for storage)');
    
    // Always initialize browser's native SpeechRecognition for UI display
    console.log('[InterviewContext] Initializing browser SpeechRecognition for UI messages');
    
    // Initialize with real callbacks to process browser transcription
    SpeechRecognitionManager.initialize(
      // Finalize transcript callback
      (transcript: string) => {
        console.log(`[InterviewContext] Browser speech recognition transcript: "${transcript}"`);
        // Update the current transcript shown to the user
        currentTranscriptRef.current = transcript;
        
        // IMPORTANT: Update messages for display to user
        // This is the ONLY place where user transcriptions are added to messages
        // Gemini transcriptions (from handleInputTranscription) are stored separately
        if (transcript && transcript.trim().length > 0) {
          // Check if we've recently added very similar text to avoid duplicates
          const now = Date.now();
          const similarText = transcript.trim() === lastTranscriptTextRef.current;
          const recentTimestamp = now - lastTranscriptTimeRef.current < 1000; // Within last second
          
          if (similarText && recentTimestamp) {
            console.log(`[InterviewContext] Skipping duplicate browser transcript: "${transcript}" (added ${now - lastTranscriptTimeRef.current}ms ago)`);
            return;
          }
          
          const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
          const isRecent = lastMessage && 
                          lastMessage.sender === 'user' && 
                          (Date.now() - new Date(lastMessage.timestamp).getTime() < 5000);
          
          if (isRecent) {
            // Update existing message
            // Commenting out for now.
            // setMessages(prev => {
            //   const updatedMessages = [...prev];
            //   const lastIndex = updatedMessages.length - 1;
              
            //   updatedMessages[lastIndex] = {
            //     ...updatedMessages[lastIndex],
            //     text: transcript.trim(),
            //     timestamp: new Date().toISOString()
            //   };
              
            //   return updatedMessages;
            // });
          } else if (transcript.trim() !== currentTranscriptRef.current.trim()) {
            // Only add new message if transcript has changed significantly
            // Commenting out for now.
            // setMessages(prev => [...prev, {
            //   id: uuidv4(),
            //   text: transcript.trim(),
            //   sender: 'user',
            //   timestamp: new Date().toISOString()
            // }]);
          }
          
          // Update our tracking refs
          lastTranscriptTimeRef.current = now;
          lastTranscriptTextRef.current = transcript.trim();
        }
      },
      // Set user speaking callback
      (speaking: boolean) => {
        console.log('SpeechRecognitionManager speaking state callback', speaking)
        userSpeakingRef.current = speaking;
        setUserSpeaking(speaking);
      },
      // Set is listening callback
      (listening: boolean) => {
        setIsListening(listening);
      },
      // User turn complete callback
      handleUserTurnComplete
    );
    
    // Start recognition immediately if microphone is enabled
    if (isMicEnabled && !isMicMuted) {
      setTimeout(() => {
        if (speechRecognitionEnabled) {
        SpeechRecognitionManager.start();
        }
      }, 500);
    }
    
    return null;
  }, [isMicEnabled, isMicMuted, handleUserTurnComplete, messages, setMessages, speechRecognitionEnabled]);
  
  // Update the cleanup function to use our singleton
  const cleanupSpeechRecognition = useCallback(() => {
    if (!speechRecognitionEnabled) {
      console.log('[InterviewContext] Speech recognition disabled, skipping cleanup');
      return;
    }
    
    console.log('[InterviewContext] Cleaning up speech recognition through singleton manager');
    // Just call the stop method - since we've replaced it with a no-op function,
    // this won't actually try to call the browser's SpeechRecognition
    SpeechRecognitionManager.stop();
  }, [speechRecognitionEnabled]);

  // Update the speech recognition setup effect - now uses our singleton manager
  useEffect(() => {
    if (!speechRecognitionEnabled) {
      console.log('[InterviewContext] Speech recognition disabled, skipping setup');
      return;
    }
    
    console.log('[InterviewContext] Setting up speech recognition in hybrid mode');
    
    // Initialize browser speech recognition with callbacks
    SpeechRecognitionManager.initialize(
      // Transcript callback
      (transcript: string) => {
        console.log(`[InterviewContext] Browser recognition transcript: "${transcript}"`);
        currentTranscriptRef.current = transcript;
      },
      // Speaking state callback
      (speaking: boolean) => {
        console.log('SpeechRecognitionManager speaking state callback', speaking)
        userSpeakingRef.current = speaking;
        setUserSpeaking(speaking);
      },
      // Listening state callback
      (listening: boolean) => {
        setIsListening(listening);
      },
      // User turn complete callback
      handleUserTurnComplete,
      // Pass the current mute state
      isMicMuted
    );
    
    // Let's set a delayed start to avoid the problem of multiple mount/unmount cycles
    let startTimer: NodeJS.Timeout | null = null;
    
    if (isMicEnabled && !isMicMuted) {
      // Use a longer timeout to ensure the component is fully mounted and stable
      startTimer = setTimeout(() => {
        console.log('[InterviewContext] Delayed start of speech recognition after page stabilizes');
        setIsListening(true);
        if (speechRecognitionEnabled) {
        SpeechRecognitionManager.start();
        }
      }, 2000); // 2 second delay to let all the mount/unmount cycles complete
    }
    
    // Clean up on unmount
    return () => {
      if (startTimer) {
        clearTimeout(startTimer);
      }
      console.log('[InterviewContext] Component unmounting, cleaning up speech recognition');
      if (speechRecognitionEnabled) {
      SpeechRecognitionManager.stop();
      }
    };
  }, [isMicEnabled, isMicMuted, handleUserTurnComplete, speechRecognitionEnabled]);
  
  // Create ref outside of the effect
  const prevIsListeningRef = useRef(isListening);
  
  // Add dedicated effect for isListening state changes
  useEffect(() => {
    if (!speechRecognitionEnabled) {
      return;
    }
    
    // Skip effect during the initial mount and early render cycles
    if (!isMicEnabled || isMicMuted) return;
    
    // Only proceed if the state actually changed and isn't just an initial mount
    if (prevIsListeningRef.current !== isListening) {
      prevIsListeningRef.current = isListening;
      
      // Handle listening state changes
      if (isListening) {
        console.log('[InterviewContext] isListening changed to true, ensuring speech recognition is active');
        
        // Use a slight delay to avoid conflicts with other effects
        setTimeout(() => {
          if (SpeechRecognitionManager.start) {
            SpeechRecognitionManager.start();
          }
        }, 500);
      } else {
        console.log('[InterviewContext] isListening changed to false, ensuring speech recognition is stopped');
        if (SpeechRecognitionManager.stop) {
          SpeechRecognitionManager.stop();
        }
      }
    }
  }, [isListening, isMicEnabled, isMicMuted, speechRecognitionEnabled]);

  // Add an effect to handle isMicEnabled changes
  const isInitialMountRef = useRef(true);
  
  useEffect(() => {
    if (!speechRecognitionEnabled) {
      return;
    }
    
    // Skip immediate restart during initial mount to prevent conflicts
    // with the delayed start in the main effect
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    
    if (isMicEnabled && !isMicMuted) {
      if (!isListening) {
        console.log('[InterviewContext] Microphone enabled, setting isListening to true');
        setIsListening(true);
        
        // Use a slight delay to avoid race conditions
        setTimeout(() => {
          // First ensure any existing instance is stopped
          if (SpeechRecognitionManager.stop) {
            SpeechRecognitionManager.stop();
          }
          
          // Then start with a delay
          setTimeout(() => {
            if (SpeechRecognitionManager.start) {
              console.log('[InterviewContext] Starting speech recognition after microphone enabled');
              SpeechRecognitionManager.start();
            }
          }, 300);
        }, 200);
      }
    } else {
      if (isListening) {
        console.log('[InterviewContext] Microphone disabled or muted, stopping recognition');
        setIsListening(false);
        if (SpeechRecognitionManager.stop) {
          SpeechRecognitionManager.stop();
        }
      }
    }
  }, [isMicEnabled, isMicMuted, isListening, speechRecognitionEnabled]);

  // Handle incoming content from the model
  const handleContent = useCallback((data: any) => {
    if (!data || !data.modelTurn || !Array.isArray(data.modelTurn.parts)) {
      console.warn('[InterviewContext] handleContent: invalid data shape', data);
      return;
    }
    
    // Process text message content (structured text from AI via function calling)
    const textMessageParts = data.modelTurn.parts.filter((part: any) => 
      part.textMessageData && part.text
    );
    
    console.log(`[InterviewContext] handleContent: ${data.modelTurn.parts.length} part(s), ${textMessageParts.length} text_message part(s)`);
    
    if (textMessageParts.length > 0) {
      // Handle structured text messages (from function calling)
      textMessageParts.forEach((part: any) => {
        console.group('%c[InterviewContext] 📨 text_message part received', 'color: #a855f7; font-weight: bold');
        console.log('message_type:', part.textMessageData?.message_type);
        console.log('language:', part.textMessageData?.language ?? 'N/A');
        console.log('message length:', part.text?.length);
        console.log('message preview:', part.text?.substring(0, 150));
        console.log('full textMessageData:', part.textMessageData);
        console.groupEnd();
        
        setMessages(prev => {
          console.log(`[InterviewContext] ✅ Adding text_message to messages array (total: ${prev.length + 1})`);
          return [...prev, { 
            id: uuidv4(), 
            text: part.text, 
            sender: 'ai' as const,
            timestamp: new Date().toISOString(),
            messageType: 'text_message',
            textMessageData: part.textMessageData
          }];
        });
      });
      
      return; // Don't process as regular text if it's a text message
    }
    
    // Process regular text content
    const textParts = data.modelTurn.parts.filter((part: any) => 
      part.text && typeof part.text === 'string' && !part.textMessageData
    );
    
    if (textParts.length > 0) {
      const combinedText = textParts.map((part: any) => part.text).join(' ');
      
      if (combinedText.trim()) {
        // Add to messages with AI as sender
        // Commenting out for now.
        // setMessages(prev => [...prev, { 
        //   id: uuidv4(), 
        //   text: combinedText, 
        //   sender: 'ai',
        //   timestamp: new Date().toISOString()
        // }]);
      }
    }
  }, [setMessages]);

  // Add handler for transcription events
  const handleTranscription = useCallback((text: string) => {
    console.log('[InterviewContext] Received transcription:', text);
    
    // Don't update if the text is empty
    if (!text || text.trim().length === 0) return;
    
    // Set AI speaking flag to true when receiving transcription
    if (!aiSpeakingRef.current) {
      aiSpeakingRef.current = true;
      // Set transcript to empty string since AI was silent before and now it's speaking
      setModelTranscript('');
    }
    
    const isNewMessage = !modelTranscript;
    
    // Update the UI state for current transcription using a more sophisticated approach
    setModelTranscript(prev => {
      // If this is a new message or prev was empty, just use the new text
      if (isNewMessage || !prev) {
        return text;
      }
      
      // If the new text is substantially longer, it's likely a better/more complete version
      if (text.length > prev.length * 1.2) {
        return text;
      }
      
      // Check if the new text is a continuation of what we already have
      // This handles the case where the model is sending word-by-word updates
      if (text.length >= prev.length && text.startsWith(prev.substring(0, Math.min(10, prev.length)))) {
        return text;
      }
      
      // For shorter updates that don't match the beginning of the existing text,
      // check if they could be continuing from the end
      const lastFewWords = prev.split(' ').slice(-3).join(' ');
      if (text.startsWith(lastFewWords) && text.length > lastFewWords.length) {
        // This is a continuation from where we left off
        return prev + text.substring(lastFewWords.length);
      }
      
      // Usually AI doesnt send spacing after punctuation, so we add a space if the previous character is a punctuation mark
      const needsSpace = prev.endsWith('!') || text.startsWith('.') || text.startsWith('?') || text.startsWith(',');
      return prev + (needsSpace ? ' ' : '') + text;
    });
    
  }, [setModelTranscript, modelTranscript]);

  // Add handler for turn completion
  const handleTurnComplete = useCallback(() => {
    console.log('[InterviewContext] Turn complete - finalizing AI transcription');
    
    // Reset AI speaking state when turn is complete
    aiSpeakingRef.current = false;
    
    // When the turn is complete, ensure any final transcription gets added to messages
    if (modelTranscript && modelTranscript.trim()) {
      console.log('[InterviewContext] Adding final AI transcription to messages:', modelTranscript);
      
      // Always add the final transcription to messages on turn completion
      // Commenting out for now.
      // setMessages(prev => {
      //   // Check if the last message was from AI and contains similar content
      //   const lastMessage = prev.length > 0 ? prev[prev.length - 1] : null;
      //   const isRecentAiMessage = lastMessage && 
      //                            lastMessage.sender === 'ai' && 
      //                            (Date.now() - new Date(lastMessage.timestamp).getTime() < 3000);
        
      //   // If we have a recent AI message, update it instead of creating a new one
      //   if (isRecentAiMessage) {
      //     // Use the longer of the two texts
      //     const betterText = modelTranscript.length > lastMessage.text.length ? 
      //                        modelTranscript : lastMessage.text;
          
      //     const updatedMessages = [...prev];
      //     updatedMessages[updatedMessages.length - 1] = {
      //       ...updatedMessages[updatedMessages.length - 1],
      //       text: betterText,
      //       timestamp: new Date().toISOString()
      //     };
      //     return updatedMessages;
      //   }
        
      //   setGeminiTranscriptions(prev => [...prev, {
      //     id: uuidv4(),
      //     text: modelTranscript,
      //     sender: 'ai',
      //     timestamp: new Date().toISOString()
      //   }]);
      //   // Otherwise create a new message
      //   return [...prev, { 
      //     id: uuidv4(), 
      //     text: modelTranscript, 
      //     sender: 'ai',
      //     timestamp: new Date().toISOString()
      //   }];
      // });
      
      // Reset model transcript after adding to messages
      // Commenting out for now
      // setModelTranscript('');
    }
    
    // Always notify SpeechRecognitionManager that AI is no longer speaking
    if (speechRecognitionEnabled) {
    SpeechRecognitionManager.setAISpeaking(false);
    }
    
    // Ensure speech recognition is active after AI finishes talking
    if (speechRecognitionEnabled && isMicEnabled && !isMicMuted) {
      console.log('[InterviewContext] AI finished speaking, ensuring speech recognition is active');
      
      // Enforce a delay to ensure all audio processing is complete
      setTimeout(() => {
        if (!isListening) {
          setIsListening(true);
        }
        
        // Make sure to create a fresh instance and start it
        SpeechRecognitionManager.stop();
        
        // Add a short delay before restarting to avoid audio conflicts
        setTimeout(() => {
          SpeechRecognitionManager.start();
        }, 200);
      }, 300);
    }
  }, [modelTranscript, setMessages, setModelTranscript, isMicEnabled, isMicMuted, isListening, setIsListening, speechRecognitionEnabled]);
  
  // Add handler for input transcription (user speech detected by the backend)
  const handleInputTranscription = useCallback((text: string) => {
    console.log(`[InterviewContext] 'input_transcription' event: "${text}"`);
    
    if(!userSpeaking) {
      setUserSpeaking(true);
      console.log('[InterviewContext] User speaking detected, setting to true');
    }
    // Only store Gemini transcriptions for later use in results/feedback
    if (text && text.trim()) {
      // Initialize gemini transcription chunks array if needed
      if (!window.geminiTranscriptionChunks) {
        window.geminiTranscriptionChunks = [];
      }
      
      // Store raw chunk
      window.geminiTranscriptionChunks.push(text.trim());
      
      console.log("[InterviewContext] Adding Gemini Transcription:", text.trim());
      // Add to our state array for access through context (for feedback generation)
      // Commenting out for now.
      // setGeminiTranscriptions(prev => [...prev, {
      //   id: uuidv4(),
      //   text: text.trim(),
      //   sender: 'user',
      //   timestamp: new Date().toISOString()
      // }]);
      
      console.log(`[InterviewContext] Stored Gemini transcription: "${text}"`);
      // Update speaking state only - DO NOT ADD TO MESSAGES
      // Messages should only come from browser's SpeechRecognition
      userSpeakingRef.current = true;
      
      // Reset speaking state after delay
      if (window.messageFinalizationTimer) {
        clearTimeout(window.messageFinalizationTimer);
      }
      
      window.messageFinalizationTimer = setTimeout(() => {
        // Reset user speaking state
        setUserSpeaking(false);
        userSpeakingRef.current = false;
      }, 1500);
    }
  }, [messages, setUserSpeaking, setMessages ]);
  
  // Set up client event listeners
  useEffect(() => {
    if (!liveAPI?.client) return;
    
    // console.log('[InterviewContext] Setting up client event listeners');
    
    // Register handlers for each client event
    const onContent = (data: any) => {
      console.log(`[InterviewContext] 'content' event:`, data);
      handleContent(data);
    };
    
    const onAudio = (data: any) => {
      handleAudio(data);
    };
    
    const onTurnComplete = () => {
      console.log(`[InterviewContext] 'turncomplete' event`);
      handleTurnComplete();
    };
    
    const onThinking = (isThinking: boolean) => {
      console.log(`[InterviewContext] 'thinking' event: ${isThinking}`);
      setIsInterviewerThinking(isThinking);
    };
    
    // Handles transcription events for AI Interviewer.
    const onTranscription = (text: string) => {
      if(userSpeaking) {
        setUserSpeaking(false);
        console.log('[InterviewContext] User speaking detected, setting to false since AI is speaking');
      }
      console.log(`[InterviewContext] 'transcription' event: "${text}"`);
      handleTranscription(text);
    };
    
    // Add handler for interruption events
    const onInterrupted = () => {
      console.log('[InterviewContext] Interruption detected - stopping AI audio and prioritizing user input');
      
      // Stop AI speech playback
      const streamer = getAudioStreamer();
      if (streamer) {
        streamer.stop();
      }
      
      // Reset AI speaking state
      aiSpeakingRef.current = false;
      
      // Clear model transcript to visually indicate the interruption
      // Commenting out for now
      // setModelTranscript('');
      
      // Ensure speech recognition is active for user
      if (speechRecognitionEnabled && isMicEnabled && !isMicMuted) {
        console.log('[InterviewContext] Re-enabling speech recognition after interruption');
        SpeechRecognitionManager.setAISpeaking(false);
        setTimeout(() => {
          if (!isListening) {
            setIsListening(true);
          }
          SpeechRecognitionManager.start();
        }, 100);
      }
    };
    
    liveAPI.client.on('content', onContent);
    liveAPI.client.on('audio', onAudio);
    liveAPI.client.on('turncomplete', onTurnComplete);
    liveAPI.client.on('transcription', onTranscription);
    liveAPI.client.on('input_transcription', handleInputTranscription);
    liveAPI.client.on('interrupted', onInterrupted);
    liveAPI.client.on('thinking', onThinking);
    
          return () => {
        // console.log('[InterviewContext] Removing client event listeners');
        if (liveAPI?.client) {
          liveAPI.client.off('content', onContent);
          liveAPI.client.off('audio', onAudio);
          liveAPI.client.off('turncomplete', onTurnComplete);
          liveAPI.client.off('transcription', onTranscription);
          liveAPI.client.off('input_transcription', handleInputTranscription);
          liveAPI.client.off('interrupted', onInterrupted);
          liveAPI.client.off('thinking', onThinking);
        }
      };
  }, [liveAPI?.client, handleContent, handleAudio, handleTurnComplete, handleTranscription, handleInputTranscription, isMicEnabled, isMicMuted, isListening, setIsListening, setModelTranscript, speechRecognitionEnabled]);
  
  // Track actual connection state
  const [actualConnected, setActualConnected] = useState(false);
  const reconnectAttemptRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 3;
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionAttemptInProgressRef = useRef(false);
  const connectionStabilityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastConnectionAttemptRef = useRef<number>(0);
  const remountProtectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Function to initialize LiveAPI connection
  const initializeConnection = useCallback(async () => {
    console.log('[InterviewContext] Initializing connection...');
    
    // Check if too soon after previous attempt
    const now = Date.now();
    const timeSinceLastAttempt = now - lastConnectionAttemptRef.current;
    
    if (timeSinceLastAttempt < 2000 && reconnectAttemptRef.current > 0) {
      console.log('[InterviewContext] Skipping connection attempt - too soon after previous attempt');
      return false;
    }
    lastConnectionAttemptRef.current = now;
    
    if (connectionAttemptInProgressRef.current) {
      console.log('[InterviewContext] Connection attempt already in progress, waiting...');
      return false;
    }
    
    connectionAttemptInProgressRef.current = true;
    
    try {
      // First check if there's an active global WebSocket connection
      const clientId = sessionStorage.getItem('mocktrail_websocket_client_id');
      
      // Check if the client ID is the same as in any active connections
      if (typeof window !== 'undefined' && window.mocktrailWsConnections && window.mocktrailWsConnections.length > 0) {
        // Look for any active connections with the same client ID
        const activeConnections = window.mocktrailWsConnections.filter(
          socket => socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)
        );
        
        if (activeConnections.length > 0) {
          console.log(`[InterviewContext] Found ${activeConnections.length} active global WebSocket connections`);
          
          // If our client has a connection to the right client ID, check if it's connected
          if (liveAPI?.client) {
            // Get the session ID from the client, Example persistentClientId__userInterviewId
            const sessionClientId = liveAPI.client.getInterviewSessionId?.().split('__')[0];
            if (sessionClientId && sessionClientId === clientId) {
              console.log(`[InterviewContext] Client ID matches persistent ID: ${sessionClientId}`);
              
              // If already connected, we're good
              if (liveAPI.client.isConnected()) {
                console.log('[InterviewContext] Already connected, no need to reconnect');
                setActualConnected(true);
                connectionAttemptInProgressRef.current = false;
                return true;
              }
              
              // If connecting, wait for it
              if (liveAPI.client.isConnecting?.()) {
                console.log('[InterviewContext] Session is already connecting, waiting for connection...');
                await new Promise(resolve => setTimeout(resolve, 500));
                connectionAttemptInProgressRef.current = false;
                return liveAPI.client.isConnected();
              }
            }
          }
        }
      }
      
      // Skip if connection is already established
      if (liveAPI?.client?.ws?.readyState === WebSocket.OPEN) {
        console.log('[InterviewContext] Already connected, skipping reconnection');
        connectionAttemptInProgressRef.current = false;
        setActualConnected(true);
        return true;
      }
      
      // Attempt to connect and await result
      console.log('[InterviewContext] Connecting to LiveAPI...');
      await liveAPI?.connect();
      
      // Only update state if we have a valid client with open WebSocket
      const isConnected = liveAPI?.client?.ws?.readyState === WebSocket.OPEN;
      
      console.log(`[InterviewContext] Connection success: ${isConnected}`);
      setActualConnected(isConnected);
      
      // Reset reconnect attempt counter on success
      if (isConnected) {
        reconnectAttemptRef.current = 0;
        
        // Clear any existing stability timeout
        if (connectionStabilityTimeoutRef.current) {
          clearTimeout(connectionStabilityTimeoutRef.current);
          connectionStabilityTimeoutRef.current = null;
        }
        
        // Add a stability timeout to wait before reconnection attempts
        connectionStabilityTimeoutRef.current = setTimeout(() => {
          // console.log('[InterviewContext] Connection stability period ended');
          connectionStabilityTimeoutRef.current = null;
        }, 10000); // 10 second stability period
      } else {
        // If connection failed, increase reconnect attempt counter
        reconnectAttemptRef.current++;
      }
      
      connectionAttemptInProgressRef.current = false;
      return isConnected;
    } catch (error) {
      console.error('[InterviewContext] Error connecting to LiveAPI:', error);
      // Increment counter on error
      reconnectAttemptRef.current++;
      connectionAttemptInProgressRef.current = false;
      setActualConnected(false);
      return false;
    }
  }, [liveAPI, setActualConnected]);
  
  // Ensure connection is established on component mount
  useEffect(() => {
    // Add a debounce mechanism to avoid multiple rapid connection attempts
    let connectionAttemptDebounceTimer: NodeJS.Timeout | null = null;
    
    // Check if another connection attempt was made very recently (within last 2 seconds)
    // This prevents rapid reconnection cycling during component remounts
    const now = Date.now();
    if (now - lastConnectionAttemptRef.current < 2000) {
      console.log('[InterviewContext] Skipping connection attempt - too soon after previous attempt');
      return;
    }
    
    lastConnectionAttemptRef.current = now;
    
    const initializeConnection = async () => {
      // If component is already unmounting, don't attempt connection
      if (!isMountedRef.current) {
        console.log('[InterviewContext] Not initializing connection - component unmounting');
        return;
      }
      
      if (!liveAPI.client) {
        console.log('[InterviewContext] Not initializing connection - client missing');
        return;
      }
      
      // If already connected according to liveAPI AND the WebSocket is actually connected,
      // we don't need to reconnect
      if (liveAPI?.client?.ws?.readyState === WebSocket.OPEN) {
        console.log('[InterviewContext] Already connected, skipping redundant connection');
        setActualConnected(true);
        setConnectionHealthy(true);
        return;
      }
      
      // Skip if we're already attempting to connect
      if (connectionAttemptInProgressRef.current) {
        console.log('[InterviewContext] Skipping connection attempt - another attempt is already in progress');
        return;
      }
      
      // Check if we already have a quota exceeded error 
      if (connectionErrorType === 'quota_exceeded' && connectionErrorVisible) {
        console.log('[InterviewContext] Skipping connection attempt due to quota exceeded error');
        return;
      }
      
      // Use global registry to check for existing connections in any state
      if (typeof window !== 'undefined' && window.mocktrailWsConnections) {
        // Count active connections
        const activeConnections = window.mocktrailWsConnections.filter(socket => 
          socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)
        ).length;
        
        // Log a warning if there are multiple active connections
        if (activeConnections > 1) {
          console.warn(`[InterviewContext] Multiple active WebSocket connections detected: ${activeConnections}`);
        }
      }
      
      console.log('[InterviewContext] Initializing WebSocket connection');
      
      try {
        // Mark connection attempt in progress
        connectionAttemptInProgressRef.current = true;
        
        // Mark this instance as the one responsible for the connection
        wasConnectedRef.current = true;
        
        // Add a stability timeout to prevent immediate reconnection attempts after successful connection
        if (connectionStabilityTimeoutRef.current) {
          clearTimeout(connectionStabilityTimeoutRef.current);
          connectionStabilityTimeoutRef.current = null;
        }
        
        await liveAPI.connect();
        console.log('[InterviewContext] WebSocket connection established successfully');
        setConnectionHealthy(true);
        setActualConnected(true);
        
        // Set a stability timeout to avoid rapid reconnection attempts
        connectionStabilityTimeoutRef.current = setTimeout(() => {
          connectionStabilityTimeoutRef.current = null;
        }, 3000); // 3 second stability period
        
        // Reset reconnection attempts on success
        reconnectAttemptRef.current = 0;
      } catch (error) {
        console.error('[InterviewContext] Failed to establish WebSocket connection:', error);
        setConnectionHealthy(false);
        setActualConnected(false);
        
        // Check for quota exceeded error - more comprehensive check
        const errorMessage = error instanceof Error ? error.message : String(error);
        
        // Enhanced check for quota errors - look for multiple keywords
        const isQuotaError = 
          errorMessage.toLowerCase().includes('quota') || 
          errorMessage.toLowerCase().includes('exceeded') || 
          errorMessage.toLowerCase().includes('limit') ||
          // Check if the client's static method reports quota exceeded
          (liveAPI.client && 
           typeof (liveAPI.client.constructor as any).isQuotaExceeded === 'function' && 
           (liveAPI.client.constructor as any).isQuotaExceeded());
        
        if (isQuotaError) {
          console.log('[InterviewContext] Quota exceeded error detected');
          setConnectionErrorType('quota_exceeded');
          
          // Ensure we track this globally
          if (liveAPI.client && 
             typeof (liveAPI.client.constructor as any).setQuotaExceeded === 'function') {
            (liveAPI.client.constructor as any).setQuotaExceeded(true);
                }
              } else {
          // Set error state if connection fails
          setConnectionErrorType('connection_error');
        }
        setConnectionErrorVisible(true);
      } finally {
        // Mark connection attempt as complete after a short delay
        // This prevents immediate reconnection attempts
              setTimeout(() => {
        connectionAttemptInProgressRef.current = false;
      }, 1000);
    }
  };
  
  // Start connection initialization with debounce
  if (connectionAttemptDebounceTimer) {
    clearTimeout(connectionAttemptDebounceTimer);
  }
  
  connectionAttemptDebounceTimer = setTimeout(() => {
    initializeConnection();
  }, 500); // 500ms debounce to prevent rapid reconnection attempts
  
  // Clean up connection on unmount if this instance established it
  return () => {
    // Clear the debounce timer
    if (connectionAttemptDebounceTimer) {
      clearTimeout(connectionAttemptDebounceTimer);
      connectionAttemptDebounceTimer = null;
    }
    
    // Clear the stability timeout
    if (connectionStabilityTimeoutRef.current) {
      clearTimeout(connectionStabilityTimeoutRef.current);
      connectionStabilityTimeoutRef.current = null;
    }
    
    // Only perform disconnect if this component was responsible for connecting
    if (wasConnectedRef.current && liveAPI.client) {
      console.log('[InterviewContext] Cleaning up WebSocket connection on unmount');
      
      // Don't try to disconnect if we're in quota exceeded state
      if (connectionErrorType === 'quota_exceeded') {
        console.log('[InterviewContext] Skipping disconnect due to quota exceeded state');
        return;
      }
      
      // Only attempt disconnect if client reports it's connected
      // Use both the connected state and the WebSocket readyState to avoid race conditions
      try {
        const isConnected = liveAPI.client.isConnected ? liveAPI.client.isConnected() : false;
        if (liveAPI.connected && isConnected) {
          liveAPI.disconnect().catch(err => {
            console.error('[InterviewContext] Error disconnecting WebSocket:', err);
      });
    } else {
          console.log('[InterviewContext] Not disconnecting - WebSocket already disconnected');
        }
      } catch (err) {
        console.warn('[InterviewContext] Error checking connection state during cleanup:', err);
      }
    }
  };
}, [liveAPI.client, liveAPI.connected, liveAPI.connect, liveAPI.disconnect, connectionErrorType, connectionErrorVisible]);
  

  // Protection against remounting loop
  useEffect(() => {
    // Check if we've been remounted too many times in a short period
    if (typeof window !== 'undefined' && window.mocktrailMountCycles) {
      const recentMounts = window.mocktrailMountCycles.filter(
        cycle => cycle.component === 'InterviewProvider' && 
                Date.now() - cycle.mountTime < 10000 // Last 10 seconds
      );
      
      if (recentMounts.length > 3) {
        console.warn(`[InterviewContext] WARNING: ${recentMounts.length} provider mounts detected in the last 10 seconds!`);
        
        // If we're in a remount loop, delay connection attempts
        if (!remountProtectionTimeoutRef.current) {
          console.log('[InterviewContext] Entering remount protection mode - delaying connection attempts');
          connectionAttemptInProgressRef.current = true;
          
          // Set a timeout to clear the protection after a while
          remountProtectionTimeoutRef.current = setTimeout(() => {
            console.log('[InterviewContext] Exiting remount protection mode');
            connectionAttemptInProgressRef.current = false;
            remountProtectionTimeoutRef.current = null;
          }, 3000); // 3 seconds of protection
        }
      }
    }
    
    return () => {
      if (remountProtectionTimeoutRef.current) {
        clearTimeout(remountProtectionTimeoutRef.current);
        remountProtectionTimeoutRef.current = null;
      }
    };
  }, []);
  
  // Handle end interview
  const handleEndInterview = useCallback(() => {

    setShowEndInterviewConfirmation(true);
  }, [elapsedTime, userInterviewId]);
  
  // Confirm end interview
  const confirmEndInterview = useCallback(async (finalElapsedTime: number) => {
    setShowEndInterviewConfirmation(false);
    
    // FIRST PRIORITY: Stop all AI audio playback immediately before doing anything else
    console.log('[InterviewContext] === IMMEDIATE AUDIO CLEANUP - Stopping all AI audio ===');
    
    // Stop audio through the client if available
    if (liveAPI.client && typeof liveAPI.client.stopAllAudio === 'function') {
      console.log('[InterviewContext] Calling stopAllAudio on client');
      liveAPI.client.stopAllAudio();
    }
    
    // Also trigger the interrupt function from the API hook
    if (liveAPI.interruptPlayback) {
      console.log('[InterviewContext] Calling interruptPlayback from liveAPI hook');
      liveAPI.interruptPlayback();
    }
    
    // Emergency global audio stop as additional safety measure
    try {
      console.log('[InterviewContext] Calling emergency audio stop via window');
      if (typeof window !== 'undefined' && (window as any).stopAudioStreamer) {
        (window as any).stopAudioStreamer();
      }
    } catch (err) {
      console.warn('[InterviewContext] Error calling global audio stop:', err);
    }
    
    // Additional safety: set AI speaking state to false via speech recognition
    if (speechRecognitionEnabled) {
      try {
        SpeechRecognitionManager.setAISpeaking(false);
        console.log('[InterviewContext] Set AI speaking to false via SpeechRecognitionManager');
      } catch (err) {
        console.warn('[InterviewContext] Error setting AI speaking to false:', err);
      }
    }
    
    console.log('[InterviewContext] === AUDIO CLEANUP COMPLETE - Proceeding with interview end ===');
    
    // Make sure connection error is hidden when exiting interview
    setConnectionErrorVisible(false);
    
    // Set exiting flag to prevent any unwanted navigation or reconnections
    setIsExitingInterview(true);
    console.log('[InterviewContext] Setting isExitingInterview to TRUE');
    
    // Reset connection state to prevent reconnection attempts during exit
    setConnectionHealthy(true);
    
    console.log('[InterviewContext] FINAL determined elapsed time:', finalElapsedTime);
    
    // Stop timers
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    
    console.log('[InterviewContext] Ending interview Id:', interviewDetails?.id || interviewId);
    
    // Generate a unique ID for this cleanup operation for logging
    const cleanupId = Math.random().toString(36).substring(2, 8);
    
    // Do speech recognition cleanup first to prevent it from continuing to run
    console.log(`[InterviewContext][${cleanupId}] Performing complete speech recognition cleanup`);
    if (speechRecognitionEnabled) {
    SpeechRecognitionManager.cleanup();
    }
    
    // Set state to indicate that the microphone is disabled
    // This helps prevent speech recognition from trying to restart
    setIsMicEnabled(false);
    setIsListening(false);
    setIsCameraEnabled(false);
    setIsScreenSharing(false);
      // Disconnect from the API first to prevent reconnection attempts
      try {
        console.log('[InterviewContext] Disconnecting from API before generating feedback');
        if (liveAPI.connected && liveAPI.disconnect) {
          await liveAPI.disconnect();
          console.log('[InterviewContext] API disconnected successfully');
        }
      } catch (error) {
        console.error('[InterviewContext] Error during API disconnect:', error);
      }
      
      // Import the FeedbackService for generating interview feedback
      try {
          // Show generating feedback modal
          setIsGeneratingFeedback(true);
          console.log('[InterviewContext] Generating feedback from transcriptions...');
          const feedbackService = FeedbackService.getInstance();
          
          // Update the status & minutes used of the interviewDetails
          if (interviewDetails) {
            // Only update the minutes used, not the status (let App.tsx handle the status update)

            console.log('[InterviewContext] ABSOLUTELY FINAL elapsed time (seconds):', finalElapsedTime);
            
            // Store the actual elapsed time in seconds
            interviewDetails.minutesUsed = finalElapsedTime;
            
            console.log('[InterviewContext] Updated minutes used:', interviewDetails.minutesUsed);
            console.log('[InterviewContext] Updated interview details:', interviewDetails);
          }

          const interviewDetailsForFeedback = interviewDetails
          console.log(`[InterviewContext] Generating feedback with InterviewDetails(userInterviewId, title, company, jobRole, duration, minutesUsed) ${interviewDetailsForFeedback.userInterviewId}, ${interviewDetailsForFeedback.title}, ${interviewDetailsForFeedback.company}, ${interviewDetailsForFeedback.jobRole}, ${interviewDetailsForFeedback.duration}, ${interviewDetailsForFeedback.minutesUsed}`);

          // Check if this is an implementor interview
          const isImplementorInterview = !!interviewDetailsForFeedback.implementorUniqueId;

          // Generate feedback using the appropriate service method
          let feedback;
          if (isImplementorInterview) {
            console.log('[InterviewContext] [IMPLEMENTOR] Generating implementor feedback for unique_id:', interviewDetailsForFeedback.implementorUniqueId);
            feedback = await feedbackService.generateImplementorFeedback(
              interviewDetailsForFeedback,
              interviewDetailsForFeedback.userInterviewId || ''
            );
          } else {
            console.log('[InterviewContext] Generating regular feedback');
            feedback = await feedbackService.generateFeedback(
              interviewDetailsForFeedback,
              interviewDetailsForFeedback.userInterviewId || ''
            );
          }

          if (feedback.summary === undefined && feedback.categories === undefined) {
            console.log('[InterviewContext] Feedback is null, only interview details are available:', feedback);
          } else {
            console.log('[InterviewContext] Feedback is not null, full feedback is available:', feedback);
          }
          
          // Hide the generating feedback modal
          setIsGeneratingFeedback(false);
          
          // Call the callback with feedback if provided
          if (onInterviewComplete) {
            console.log('[InterviewContext] Calling onInterviewComplete with feedback:', feedback);
            try {
              await onInterviewComplete(interviewId, interviewDetails?.series?._id || undefined, true, feedback);
            } finally {
              // Always reset exit flag when complete, even if there was an error
              setIsExitingInterview(false);
            }
          } else {
            setIsExitingInterview(false);
          }
      } catch (error) {
        console.error('[InterviewContext] Error generating feedback:', error);
        
        // Hide the generating feedback modal
        setIsGeneratingFeedback(false);
        
        // Ensure callback is still called even if feedback generation fails
        if (onInterviewComplete) {
          try {
            await onInterviewComplete(interviewId, interviewDetails?.series?._id || undefined, false, null);
          } finally {
            // Always reset exit flag when complete, even if there was an error
            setIsExitingInterview(false);
          }
        } else {
          setIsExitingInterview(false);
        }
      }
    
    console.log(`[InterviewContext][${cleanupId}] Beginning media resource cleanup...`);
    
    // Clean up video and audio tracks
    if (videoStream) {
      try {
        console.log(`[InterviewContext][${cleanupId}] Video stream found, starting track cleanup...`);
        
        // First log what tracks we have for debugging
        const videoTracks = videoStream.getVideoTracks();
        const audioTracks = videoStream.getAudioTracks();
        console.log(`[InterviewContext][${cleanupId}] Found ${videoTracks.length} video tracks and ${audioTracks.length} audio tracks`);
        
        // Stop each track individually and remove from the stream
        videoTracks.forEach((track, i) => {
          try {
            console.log(`[InterviewContext][${cleanupId}] Stopping video track ${i}: ${track.id}, label: ${track.label}`);
            track.stop();
            videoStream.removeTrack(track);
          } catch (err) {
            console.error(`[InterviewContext][${cleanupId}] Error stopping video track ${i}:`, err);
          }
        });
        
        audioTracks.forEach((track, i) => {
          try {
            console.log(`[InterviewContext][${cleanupId}] Stopping audio track ${i}: ${track.id}, label: ${track.label}`);
            track.stop();
            videoStream.removeTrack(track);
          } catch (err) {
            console.error(`[InterviewContext][${cleanupId}] Error stopping audio track ${i}:`, err);
          }
        });
        
        // Clear the video element source
        if (videoRef.current) {
          try {
            console.log(`[InterviewContext][${cleanupId}] Clearing video element srcObject`);
            videoRef.current.srcObject = null;
            videoRef.current.load(); // Force reload of the video element
          } catch (err) {
            console.error(`[InterviewContext][${cleanupId}] Error clearing video element:`, err);
          }
        }
      } catch (err) {
        console.error(`[InterviewContext][${cleanupId}] Error during track cleanup:`, err);
      }

      // Add an additional step to help Chrome release camera LED
      try {
        // Request a blank stream to help reset browser state
        // IMPORTANT: We must request at least one of audio or video to avoid errors
        navigator.mediaDevices.getUserMedia({video: {width: 1, height: 1}, audio: false})
          .then(emptyStream => {
            // Immediately stop it
            emptyStream.getTracks().forEach(t => t.stop());
            console.log(`[InterviewContext][${cleanupId}] Extra cleanup: Used empty stream to help reset browser state`);
            
            // Force device enumeration refresh after cleanup
            navigator.mediaDevices.enumerateDevices()
              .then(() => console.log(`[InterviewContext][${cleanupId}] Device enumeration refreshed`))
              .catch(err => console.log(`[InterviewContext][${cleanupId}] Error refreshing device enumeration:`, err));
          })
          .catch(err => {
            console.log(`[InterviewContext][${cleanupId}] Empty stream request failed (non-critical):`, err);
            
            // Try again with audio only if video failed
            navigator.mediaDevices.getUserMedia({video: false, audio: true})
              .then(audioStream => {
                audioStream.getTracks().forEach(t => t.stop());
                console.log(`[InterviewContext][${cleanupId}] Extra cleanup: Used audio-only stream to help reset browser state`);
              })
              .catch(audioErr => {
                console.log(`[InterviewContext][${cleanupId}] Audio-only stream request also failed (non-critical):`, audioErr);
              });
          });
      } catch (err) {
        // This is an extra step, so just log and continue
        console.log(`[InterviewContext][${cleanupId}] Error during extra cleanup step (non-critical):`, err);
      }
      
      // If there's a callback for updating the video stream, set it to null
      if (onVideoStreamChange) {
        onVideoStreamChange(null);
      }
    }
    
    // Close any WebSocket connections that are stored on the window object
    if (window.mocktrailWsConnections && window.mocktrailWsConnections.length > 0) {
      console.log(`[InterviewContext] Closing ${window.mocktrailWsConnections.length} websocket connections...`);
      
      const wsConnections = [...window.mocktrailWsConnections]; // Create a copy to avoid modification issues
      
      wsConnections.forEach((ws, index) => {
        try {
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            ws.onclose = null; // Remove event handlers to prevent reconnection attempts
            ws.onerror = null;
            ws.close(1000, "Interview ended");
            console.log(`[InterviewContext] Closed websocket connection #${index}`);
          }
        } catch (err) {
          console.error(`[InterviewContext] Error closing websocket connection #${index}:`, err);
        }
      });
      
      // Clear the array
      window.mocktrailWsConnections = [];
    }
    
    // Clear any global timers
    if (window.altairTimers && window.altairTimers.length > 0) {
      console.log(`[InterviewContext] Clearing ${window.altairTimers.length} timers...`);
      window.altairTimers.forEach((timer, index) => {
        try {
          clearTimeout(timer);
          clearInterval(timer);
          console.log(`[InterviewContext] Cleared timer #${index}`);
        } catch (err) {
          console.error(`[InterviewContext] Error clearing timer #${index}:`, err);
        }
      });
      window.altairTimers = [];
    }
    
    // Reset state variables to ensure clean start if user returns to interview
    setIsCameraEnabled(false);
    setIsScreenSharing(false);
    setIsSpeaking(false);
    setUserSpeaking(false);
    
    console.log('[InterviewContext] Interview end cleanup completed for ID:', interviewDetails?.id || interviewId);
    
    // Clean up AudioContext and AudioStreamer
    if (typeof window !== 'undefined') {
      if (outputAudioContext) {
        outputAudioContext.close();
        outputAudioContext = null;
      }
      audioStreamer = null;
    }
    
    // Final check - run speech recognition cleanup again to be absolutely certain
    setTimeout(() => {
      console.log('[InterviewContext] Performing final speech recognition cleanup check');
      SpeechRecognitionManager.cleanup();
      
      // Only reset elapsed time after all cleanup and feedback processing is complete
      // This should be the last operation in the cleanup process
      console.log('[InterviewContext] Final cleanup step: Resetting elapsed time');
      setElapsedTime(0);
    }, 1000);
  }, [onInterviewComplete, interviewDetails, interviewId, videoStream, videoRef, onVideoStreamChange, liveAPI.connected, liveAPI.disconnect, speechRecognitionEnabled]);
  
  // Download transcript
  const downloadTranscript = useCallback(() => {
    if (messages.length === 0) return;
    
    // Create transcript content
    const content = [
      `Interview Transcript - ${interviewDetails?.title || 'Interview'}`,
      `Company: ${interviewDetails?.company || ''}`,
      `Job Role: ${interviewDetails?.jobRole || ''}`,
      `Date: ${new Date().toLocaleDateString()}`,
      `Duration (Used): ${formatTime(elapsedTime)}`,
      '',
      ...messages.map(m => `${m.timestamp}:${m.sender === 'user' ? 'You' : 'AI'}: ${m.text}`)
    ].join('\n');
    
    // Create download link
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `transcript_${new Date().toISOString().slice(0, 10)}.txt`;
    
    // Trigger download
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }, [messages, interviewDetails, formatTime, elapsedTime]);
  
  // Helper function to identify the type of media from a track
  const identifyMediaType = useCallback((track: MediaStreamTrack): 'camera' | 'screen' | 'tab' | 'window' | 'unknown' => {
    if (!track) return 'unknown';
    
    const label = track.label.toLowerCase();
    const settings = track.getSettings ? track.getSettings() : null;
    
    // Log detailed info for debugging
    console.log(`[InterviewContext] Identifying track: ${track.id}`);
    console.log(`[InterviewContext] Track label: ${label}`);
    console.log(`[InterviewContext] Track settings:`, settings);
    
    // Use displaySurface from settings if available (most reliable)
    if (settings && settings.displaySurface) {
      if (settings.displaySurface === 'monitor') return 'screen';
      if (settings.displaySurface === 'browser') return 'tab';
      if (settings.displaySurface === 'window') return 'window';
    }
    
    // Fall back to label-based detection
    if (label.includes('screen')) return 'screen';
    if (label.includes('display')) return 'screen';
    if (label.includes('tab')) return 'tab';
    if (label.includes('chrome')) return 'tab'; // Chrome tab sharing often includes 'chrome'
    if (label.includes('window')) return 'window';
    
    // Camera detection
    if (label.includes('camera') || label.includes('webcam') || label.includes('facetime')) {
      return 'camera';
    }
    
    // Default assumption for non-camera devices in a sharing context
    if (isScreenSharing) return 'screen';
    
    return 'unknown';
  }, [isScreenSharing]);
  
  // Message sending
  const sendUserMessage = useCallback((text: string) => {
    if (!text.trim()) return;
    
    // Add to messages with user as sender
    setMessages(prev => [...prev, { 
      id: uuidv4(), 
      text: text.trim(),
      sender: 'user',
      timestamp: new Date().toISOString()
    }]);
    
    // Send the message to the backend if connected
    if (liveAPI.connected && liveAPI.client) {
      // Since we're using a different API format now, call the modified send method
      liveAPI.client.sendRealtimeInput({
        text: text.trim()
      });
    }
  }, [liveAPI, setMessages]);
  
  // Device toggles
  const toggleCamera = useCallback(() => {
    setIsCameraEnabled(prev => {
      const newValue = !prev;
      
      // If turning on, remember the permission was granted
      if (newValue) {
        sessionStorage.setItem('hasCameraPermission', 'true');
        
        // If we're turning camera on and don't have a valid video stream, request one
        if (!videoStream || !videoStream.active || videoStream.getVideoTracks().every(track => track.readyState === 'ended')) {
          console.log('[InterviewContext] Camera enabled but no active video stream, requesting access...');
          
          // Build constraints based on device selections
          const constraints: MediaStreamConstraints = {
            video: selectedVideoDevice 
              ? { deviceId: { exact: selectedVideoDevice } } 
              : true,
            audio: isMicEnabled && selectedAudioDevice 
              ? { deviceId: { exact: selectedAudioDevice } }
              : isMicEnabled
          };
          
          // Request camera access
          navigator.mediaDevices.getUserMedia(constraints)
            .then(stream => {
              console.log('[InterviewContext] New camera stream obtained:', 
                stream.getTracks().map(t => `${t.kind}: ${t.label}`));
              
              // Update stream reference
              if (onVideoStreamChange) {
                onVideoStreamChange(stream);
              }
              
              // Update video element
              if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play()
                  .catch(err => console.warn('[InterviewContext] Error playing new camera video:', err));
              }
            })
            .catch(err => {
              console.error('[InterviewContext] Error obtaining new camera stream:', err);
              // Don't toggle back to false - user will see blank screen and can retry
            });
        } else if (videoStream) {
          // Just enable existing tracks if we have them
          videoStream.getVideoTracks().forEach(track => {
            track.enabled = true;
          });
        }
      } else if (videoStream) {
        // If turning off, disable video tracks
        videoStream.getVideoTracks().forEach(track => {
          track.enabled = false;
        });
      }
      
      return newValue;
    });
  }, [videoStream, onVideoStreamChange, videoRef, selectedVideoDevice, selectedAudioDevice, isMicEnabled]);
  
  // Toggle microphone
  const toggleMicrophone = useCallback(() => {
    if (isMicMuted) {
      // When unmuting - preserve any transcript if there is one
      if (currentTranscriptRef.current && currentTranscriptRef.current.trim().length > 0) {
        console.log('[InterviewContext] Preserving transcript during unmute:', currentTranscriptRef.current);
        // Make sure to finalize any pending transcript before unmuting
        handleUserTurnComplete(currentTranscriptRef.current);
      }
      
      console.log('[InterviewContext] Unmuting microphone');
      
      // CRITICAL FIX: Update the global state immediately
      window.isMicrophoneMuted = false;
      
      // First update both states consistently
      setIsMicMuted(false);
      setIsMicEnabled(true);
      
      // Reset transcripts to ensure a clean slate
      currentTranscriptRef.current = '';
      
      // Tell the SpeechRecognitionManager about the unmute
      if (speechRecognitionEnabled) {
      SpeechRecognitionManager.setMuted(false);
      }
      
      // Delay the restart of speech recognition to ensure the engine is ready
      setTimeout(() => {
        console.log('[InterviewContext] Restarting speech recognition after unmute');
        // Restart speech recognition if AI is not speaking
        if (speechRecognitionEnabled && !aiSpeakingRef.current) {
          SpeechRecognitionManager.start();
        }
      }, 200);
    } else {
      console.log('[InterviewContext] Muting microphone');
      
      // CRITICAL FIX: Update the global state immediately
      window.isMicrophoneMuted = true;
      
      // If there's an active transcript, finalize it before muting
      if (currentTranscriptRef.current && currentTranscriptRef.current.trim().length > 0) {
        console.log('[InterviewContext] Finalizing transcript before muting:', currentTranscriptRef.current);
        handleUserTurnComplete(currentTranscriptRef.current);
      }
      
      // Update states
      setIsMicEnabled(false);
      setIsMicMuted(true);
      
      // First mute in the speech recognition manager
      if (speechRecognitionEnabled) {
      SpeechRecognitionManager.setMuted(true);
      
      // Stop speech recognition while muted
      console.log('[InterviewContext] Pausing speech recognition during mute');
      SpeechRecognitionManager.stop();
    }
    }
  }, [isMicMuted, setIsMicMuted, setIsMicEnabled, currentTranscriptRef, aiSpeakingRef, handleUserTurnComplete, speechRecognitionEnabled]);
  
  const toggleScreenShare = useCallback(() => {
    if (isScreenSharing) {
      // If already sharing, show confirmation dialog
      setShowStopScreenShareConfirmation(true);
    } else {
      // Start screen sharing
      navigator.mediaDevices.getDisplayMedia({ video: true, audio: false })
        .then(stream => {
          // Store the original camera stream if it exists
          const originalStream = videoRef.current?.srcObject as MediaStream;
          
          // Save the original stream for later restoration
          const originalVideoStream = videoStream;
          
          // Handle screen sharing ending
          const videoTrack = stream.getVideoTracks()[0];
          
          if (videoTrack) {
            console.log('[InterviewContext] Screen sharing track obtained:', videoTrack.label);
            
            // Listen for when user stops sharing
            videoTrack.onended = () => {
              console.log('[InterviewContext] Screen sharing track ended by browser');
              setIsScreenSharing(false);
              
              // If we have the original video stream, restore it
              if (originalVideoStream && videoRef.current) {
                console.log('[InterviewContext] Restoring original camera stream after screen share ended');
                videoRef.current.srcObject = originalVideoStream;
                
                // Update the stream for the VideoSocketHandler
                if (onVideoStreamChange) {
                  onVideoStreamChange(originalVideoStream);
                }
                
                videoRef.current.play().catch(err => {
                  console.warn('[InterviewContext] Error playing video after screen share ended:', err);
                });
              }
            };
            
            // Set up video element with screen share stream
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
              videoRef.current.play().catch(err => {
                console.warn('[InterviewContext] Error playing screen share video:', err);
              });
            }
            
            // Important: Make the screen sharing stream available to the VideoSocketHandler
            // by updating the videoStream reference, but ONLY if onVideoStreamChange is available
            if (onVideoStreamChange) {
              // Create a composite stream that includes both screen and audio
              const compositeStream = new MediaStream();
              
              // Add the screen sharing video track
              compositeStream.addTrack(videoTrack);
              
              // Add audio tracks from the original stream if available
              if (originalStream && isMicEnabled) {
                const audioTracks = originalStream.getAudioTracks();
                audioTracks.forEach(track => {
                  compositeStream.addTrack(track);
                });
              }
              
              // Update the video stream reference 
              onVideoStreamChange(compositeStream);
            }
            
            setIsScreenSharing(true);
          }
        })
        .catch(error => {
          console.error('[InterviewContext] Error starting screen sharing:', error);
          // User cancelled or error occurred
          setIsScreenSharing(false);
        });
    }
  }, [isScreenSharing, videoStream, videoRef, onVideoStreamChange, isMicEnabled]);
  
  // Implement the stopScreenSharing function
  const stopScreenSharing = useCallback(async (): Promise<void> => {
    setShowStopScreenShareConfirmation(false);
    
    try {
      // Get current screen share tracks from the video element
      const currentStream = videoRef.current?.srcObject as MediaStream;
      
      if (currentStream) {
        console.log('[InterviewContext] Stopping screen sharing tracks...');
        
        // Get all video tracks (screen sharing) and stop them
        const videoTracks = currentStream.getVideoTracks();
        videoTracks.forEach(track => {
          console.log(`[InterviewContext] Stopping screen track: ${track.label}`);
          track.stop();
          currentStream.removeTrack(track);
        });
      }
      
      // First clear the video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      
      // Update state before re-establishing stream to ensure clean transition
      setIsScreenSharing(false);
      console.log('[InterviewContext] Screen sharing stopped successfully');
      
      // Re-establish camera stream with a fresh getUserMedia call if camera is enabled
      if (isCameraEnabled) {
        console.log('[InterviewContext] Re-establishing camera stream after screen sharing');
        
        // Build constraints based on device selections
        const constraints: MediaStreamConstraints = {
          video: selectedVideoDevice 
            ? { deviceId: { exact: selectedVideoDevice } } 
            : true,
          audio: selectedAudioDevice && isMicEnabled
            ? { deviceId: { exact: selectedAudioDevice } }
            : isMicEnabled
        };
        
        try {
          // Request fresh camera access
          const freshCameraStream = await navigator.mediaDevices.getUserMedia(constraints);
          console.log('[InterviewContext] Fresh camera stream obtained:', 
            freshCameraStream.getTracks().map(t => `${t.kind}: ${t.label} (${t.readyState})`));
          
          // Verify the tracks are in good state before using them
          const videoTracks = freshCameraStream.getVideoTracks();
          if (videoTracks.length === 0 || !videoTracks.some(t => t.readyState === 'live' && t.enabled)) {
            console.warn('[InterviewContext] Fresh camera stream has no valid video tracks, trying fallback');
            throw new Error('No valid video tracks in fresh camera stream');
          }
          
          // Update video stream reference
          if (onVideoStreamChange) {
            onVideoStreamChange(freshCameraStream);
          }
          
          // Attach to video element
          if (videoRef.current) {
            videoRef.current.srcObject = freshCameraStream;
            videoRef.current.play().catch(err => {
              console.warn('[InterviewContext] Error playing fresh camera video:', err);
            });
          }
        } catch (err) {
          console.error('[InterviewContext] Error obtaining fresh camera stream after screen sharing:', err);
          
          // Fallback to reusing the old stream if available
          if (videoStream && videoRef.current) {
            console.log('[InterviewContext] Falling back to existing camera stream');
            
            // Clone video tracks if they exist to avoid potential issues
            if (videoStream.getVideoTracks().length > 0) {
              const fallbackStream = new MediaStream();
              videoStream.getTracks().forEach(track => {
                fallbackStream.addTrack(track.clone());
              });
              
              if (onVideoStreamChange) {
                onVideoStreamChange(fallbackStream);
              }
              
              videoRef.current.srcObject = fallbackStream;
            } else {
              // If no video tracks, just use the stream directly
              videoRef.current.srcObject = videoStream;
            }
            
            videoRef.current.play().catch(err => {
              console.warn('[InterviewContext] Error playing fallback camera video:', err);
            });
          }
        }
      }
    } catch (error) {
      console.error('[InterviewContext] Error stopping screen sharing:', error);
      setIsScreenSharing(false);
    }
    
    return Promise.resolve();
  }, [videoRef, videoStream, isCameraEnabled, selectedVideoDevice, selectedAudioDevice, isMicEnabled, onVideoStreamChange]);
  
  // Video stream handling
  const updateVideoStream = useCallback((stream: MediaStream | null) => {
    // Update the stream
    if (onVideoStreamChange) {
      onVideoStreamChange(stream);
    }
    
    // Set video element source
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [onVideoStreamChange, videoRef]);
  
  // Device management
  const handleDeviceChange = useCallback((deviceId: string, type: 'video' | 'audio') => {
    if (type === 'video') {
      setSelectedVideoDevice(deviceId);
      
      // Request access to the new device
      navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
        audio: isMicEnabled
      })
      .then(stream => {
        // Update camera error state
        setCameraError(false);
        
        // Update stream
        if (onVideoStreamChange) {
          onVideoStreamChange(stream);
        }
        
        // Update video element
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch(error => {
        console.error('[InterviewContext] Error accessing video device:', error);
        setCameraError(true);
      });
    } else if (type === 'audio') {
      setSelectedAudioDevice(deviceId);
      
      // Request access to the new device
      navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
        video: isCameraEnabled
      })
      .then(stream => {
        // Update stream
        if (onVideoStreamChange) {
          onVideoStreamChange(stream);
        }
      })
      .catch(error => {
        console.error('[InterviewContext] Error accessing audio device:', error);
      });
    }
  }, [isCameraEnabled, isMicEnabled, onVideoStreamChange, videoRef]);
  
  // UI state toggles
  const toggleFocus = useCallback(() => {
    setShowAiAvatar(prev => !prev);
  }, []);
  
  const toggleDeviceDropdown = useCallback(() => {
    setIsDeviceDropdownOpen(prev => !prev);
  }, []);
  
  const toggleMessageInput = useCallback(() => {
    setShowMessageInput(prev => !prev);
  }, []);
  
  // Interview control
  const handleRestartInterview = useCallback(() => {
    setShowRestartConfirmation(true);
  }, []);
  
  const confirmRestartInterview = useCallback(() => {
    setShowRestartConfirmation(false);
    
    // Reset the elapsed time to 0
    setElapsedTime(0);

    console.log('[InterviewContext][RestartInterview] Starting interview restart');
    
    // Save current device states before restart
    const previousCameraState = isCameraEnabled;
    const previousMicState = isMicEnabled;
    
    console.log(`[InterviewContext][RestartInterview] Preserving device states - Camera: ${previousCameraState}, Mic: ${previousMicState}`);
    
    // If screen sharing is active, we should stop it
    if (isScreenSharing) {
      console.log('[InterviewContext][RestartInterview] Stopping active screen sharing');
      // Call our existing stopScreenSharing function without showing confirmation
      setIsScreenSharing(false);
      
      // Stop all screen share tracks
      if (videoStream) {
        videoStream.getTracks().forEach(track => {
          if (track.kind === 'video') {
            console.log(`[InterviewContext][RestartInterview] Stopping screen share track: ${track.label}`);
            track.stop();
          }
        });
      }
    }
    
    // Store camera and mic states in session storage to persist through restart
    sessionStorage.setItem('hasCameraPermission', previousCameraState.toString());
    sessionStorage.setItem('hasMicPermission', previousMicState.toString());
    
    // Only disconnect if connected
    if (liveAPI && liveAPI.connected) {
      console.log('[InterviewContext][RestartInterview] Disconnecting from API');
      try {
        // Call disconnect and handle the promise
        liveAPI.disconnect()
          .then(() => console.log('[InterviewContext][RestartInterview] API disconnected successfully'))
          .catch(err => console.error('[InterviewContext][RestartInterview] Error disconnecting from API:', err));
      } catch (err) {
        console.error('[InterviewContext][RestartInterview] Error handling disconnect:', err);
      }
    }
    
    // Stop all media tracks
    console.log('[InterviewContext][RestartInterview] Stopping all media tracks');
    
    if (videoStream) {
      const tracks = videoStream.getTracks();
      tracks.forEach(track => {
        console.log(`[InterviewContext][RestartInterview] Stopping ${track.kind} track: ${track.label}`);
        track.stop();
      });
    }
    
    // Check and stop tracks from original video stream if it exists
    if (videoRef.current && videoRef.current.srcObject instanceof MediaStream) {
      const currentStream = videoRef.current.srcObject as MediaStream;
      const tracks = currentStream.getTracks();
      tracks.forEach((track: MediaStreamTrack) => {
        if (track.readyState === 'live') {
          console.log(`[InterviewContext][RestartInterview] Stopping current stream ${track.kind} track: ${track.label}`);
          track.stop();
        }
      });
      videoRef.current.srcObject = null;
    }
    
    // Stop speech recognition if it exists
    console.log('[InterviewContext][RestartInterview] Stopping speech recognition');
    try {
      // Use a direct approach to stop any speech recognition
      if (typeof window !== 'undefined') {
        // First try to use our internal state
        if (isListening) {
          setIsListening(false);
        }
        
        // Then try to access any global speech recognition objects
        if (window.SpeechRecognition || window.webkitSpeechRecognition) {
          console.log('[InterviewContext][RestartInterview] Found speech recognition API, attempting cleanup');
          // The API exists, but we might not have an active instance
          // Setting isListening to false should trigger our hooks to clean up
        }
      }
    } catch (err) {
      console.warn('[InterviewContext][RestartInterview] Error stopping speech recognition:', err);
    }
    
    // Reset state
    setMessages([]);
    // Instead of resetting camera and mic to false, preserve their state
    setIsCameraEnabled(previousCameraState);
    setIsMicEnabled(previousMicState);
    setIsScreenSharing(false); // Always turn off screen sharing on restart
    setIsListening(false);
    setUserSpeaking(false);
    setTimerCompleted(false);
    
    // Force stop any ongoing audio playback
    if (typeof window !== 'undefined') {
      // Stop all audio elements
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach(audio => {
        try {
          audio.pause();
          audio.currentTime = 0;
        } catch (err) {
          // Ignore errors when stopping audio
        }
      });
      
      // Force close any AudioContext instances
      if (window.mocktrailAudioContexts && Array.isArray(window.mocktrailAudioContexts)) {
        window.mocktrailAudioContexts.forEach((ctx: AudioContext) => {
          try {
            if (ctx && ctx.state !== 'closed') {
              ctx.close();
            }
          } catch (err) {
            // Ignore errors when closing audio contexts
          }
        });
        window.mocktrailAudioContexts = [];
      }
    }
    
    console.log('[InterviewContext][RestartInterview] Cleanup complete, calling onRestartInterview');
    
    // Set a flag to reinitialize media right after restart
    sessionStorage.setItem('shouldReinitializeAfterRestart', 'true');
    
    if (onRestartInterview) {
      onRestartInterview();
    }
    
    // Set a timeout to request camera access again if it was enabled
    if (previousCameraState) {
      setTimeout(() => {
        console.log('[InterviewContext][RestartInterview] Re-requesting camera access after restart');
        
        // Build constraints based on device selections
        const constraints: MediaStreamConstraints = {
          video: selectedVideoDevice 
            ? { deviceId: { exact: selectedVideoDevice } } 
            : true,
          audio: previousMicState && selectedAudioDevice 
            ? { deviceId: { exact: selectedAudioDevice } }
            : previousMicState
        };
        
        // Request fresh camera access
        navigator.mediaDevices.getUserMedia(constraints)
          .then(stream => {
            console.log('[InterviewContext][RestartInterview] Fresh camera stream obtained:', 
              stream.getTracks().map(t => `${t.kind}: ${t.label} (${t.readyState})`));
            
            // Update stream reference
            if (onVideoStreamChange) {
              onVideoStreamChange(stream);
            }
            
            // Attach to video element
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
              videoRef.current.play().catch(err => {
                console.warn('[InterviewContext][RestartInterview] Error playing fresh camera video:', err);
              });
            }
          })
          .catch(err => {
            console.error('[InterviewContext][RestartInterview] Error obtaining fresh camera stream:', err);
          });
      }, 1000); // Wait 1 second after restart
    }
  }, [
    liveAPI, 
    videoStream, 
    videoRef, 
    isListening, 
    onRestartInterview, 
    isCameraEnabled, 
    isMicEnabled, 
    isScreenSharing,
    selectedVideoDevice,
    selectedAudioDevice,
    onVideoStreamChange
  ]);
  
  // Utility functions
  const getCameraStatusMessage = useCallback((): string => {
    if (cameraError) {
      return 'Camera access error. Please check permissions.';
    }
    
    if (isScreenSharing) {
      return 'Screen sharing active. Click to stop.';
    }
    
    return 'Camera is enabled. Click to disable.';
  }, [cameraError, isCameraEnabled, isScreenSharing]);
  
  // Initialize audio and video device lists
  useEffect(() => {
    const populateDeviceLists = async () => {
      try {
        console.log('[InterviewContext] Populating device lists...');
        
        // Get an initial list of devices - this might include devices without labels if permissions not granted
        let devices = await navigator.mediaDevices.enumerateDevices();
        let needPermissions = devices.some(device => 
          (device.kind === 'videoinput' || device.kind === 'audioinput') && !device.label);
        
        // If we need permissions or there are no devices with labels, try to get permissions
        if (needPermissions || devices.filter(d => d.kind === 'videoinput' && d.label).length === 0) {
          console.log('[InterviewContext] Need to request permissions to access device labels');
          
          try {
            // Try to get user media based on enabled states
            const constraints: MediaStreamConstraints = {
              audio: isMicEnabled || true, // Always try audio
              video: isCameraEnabled || true // Always try video
            };
            
            const tempStream = await navigator.mediaDevices.getUserMedia(constraints);
            console.log('[InterviewContext] Successfully obtained temporary stream for device access');
            
            // After getting permissions, re-enumerate devices
            devices = await navigator.mediaDevices.enumerateDevices();
            
            // Stop the temporary stream
            tempStream.getTracks().forEach(track => {
              track.stop();
              console.log(`[InterviewContext] Stopped temporary ${track.kind} track: ${track.label}`);
            });
          } catch (permError) {
            console.warn('[InterviewContext] Could not get full device permissions:', permError);
            // Continue with partial device list
          }
        } else {
          console.log('[InterviewContext] Already have device permissions, device list available');
        }
        
        // Process device lists - must handle both with and without labels
        const videoDevs = devices
          .filter(device => device.kind === 'videoinput')
          .map((device, index) => ({
            deviceId: device.deviceId,
            label: device.label || `Camera ${index + 1}`
          }));
        
        const audioDevs = devices
          .filter(device => device.kind === 'audioinput')
          .map((device, index) => ({
            deviceId: device.deviceId,
            label: device.label || `Microphone ${index + 1}`
          }));
        
        console.log(`[InterviewContext] Found ${videoDevs.length} video devices and ${audioDevs.length} audio devices`);
        
        // Only update state if we found devices to prevent empty lists
        if (videoDevs.length > 0) {
          setVideoDevices(videoDevs);
          
          // Set default selection if not already set
          if (!selectedVideoDevice) {
            console.log('[InterviewContext] Setting default video device');
            setSelectedVideoDevice(videoDevs[0].deviceId);
          }
        } else {
          // If no devices found but camera is enabled, this is likely an error case
          console.warn('[InterviewContext] No video devices found but camera is enabled');
        }
        
        if (audioDevs.length > 0) {
          setAudioDevices(audioDevs);
          
          // Set default selection if not already set
          if (!selectedAudioDevice) {
            console.log('[InterviewContext] Setting default audio device');
            setSelectedAudioDevice(audioDevs[0].deviceId);
          }
        } else {
          // If no devices found but mic is enabled, this is likely an error case
          console.warn('[InterviewContext] No audio devices found but microphone is enabled');
        }
      } catch (error) {
        console.error('[InterviewContext] Error populating device lists:', error);
      }
    };
    
    // Populate device lists on first mount
    populateDeviceLists();
    
    // Add listener for device changes
    const handleDeviceChange = () => {
      console.log('[InterviewContext] Device change detected, repopulating device lists');
      populateDeviceLists();
    };
    
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [isCameraEnabled, isMicEnabled, selectedVideoDevice, selectedAudioDevice]);
  
  // Setup recognition when listening state changes
  useEffect(() => {
    if (!speechRecognitionEnabled) {
      return;
    }
    
    if (isListening) {
      console.log('[InterviewContext] isListening changed to true, ensuring speech recognition is active');
      
      // Start speech recognition when listening is enabled
      SpeechRecognitionManager.start();
    } else {
      console.log('[InterviewContext] isListening changed to false, ensuring speech recognition is stopped');
      // Stop speech recognition when listening is disabled
      SpeechRecognitionManager.stop();
    }
  }, [isListening, speechRecognitionEnabled]);

  // Mute/unmute audio when AI is speaking to prevent feedback
  useEffect(() => {
    if (!speechRecognitionEnabled) {
      return;
    }
    
    // Only manipulate audio when we have a stream and microphone is enabled
    if (!videoStream || !isMicEnabled) return;
    
    // Track AI speech status - use modelTranscript as indicator
    const aiIsSpeaking = Boolean(modelTranscript && modelTranscript.trim().length > 0);
    
    // Store the current value in the ref for other components to access
    aiSpeakingRef.current = aiIsSpeaking;
    
    // Use the SpeechRecognitionManager to handle AI speaking state
    // This will pause recognition when AI is speaking and resume when it stops
    SpeechRecognitionManager.setAISpeaking(aiIsSpeaking);

    
  }, [modelTranscript, videoStream, isMicEnabled, isListening, speechRecognitionEnabled]);

  // Automatic start of speech recognition when component mounts
  useEffect(() => {
    if (!speechRecognitionEnabled) {
      console.log('[InterviewContext] Speech recognition disabled, skipping automatic start');
      return;
    }
    
    // On component mount, initialize speech recognition
    console.log('[InterviewContext] Component mounted, initializing speech recognition');
    initializeSpeechRecognition();
    
    // Only start listening if microphone is enabled
    if (isMicEnabled) {
      console.log('[InterviewContext] Microphone enabled, setting isListening to true');
      setIsListening(true);
    }
    
    // Clean up on unmount
    return () => {
      console.log('[InterviewContext] Component unmounting, cleaning up speech recognition');
      if (speechRecognitionEnabled) {
      SpeechRecognitionManager.stop();
      }
      cleanupSpeechRecognition();
    };
  }, [speechRecognitionEnabled]); // Empty dependency array ensures this runs only on mount/unmount
  
  // Add a cleanup effect when the component unmounts to ensure speech recognition is cleaned up
  useEffect(() => {
    return () => {
      // This runs when the InterviewProvider component unmounts
      console.log('[InterviewContext] InterviewProvider component unmounting, cleaning up speech recognition');
      
      // Immediately disable microphone to prevent any new recognition attempts
      setIsMicEnabled(false);
      setIsListening(false);
      
      // Ensure speech recognition is fully cleaned up
      if (speechRecognitionEnabled) {
      SpeechRecognitionManager.cleanup();
      }
      
      // Clean up any other audio resources
      if (outputAudioContext) {
        try {
          outputAudioContext.close();
          outputAudioContext = null;
        } catch (err) {
          console.error('[InterviewContext] Error closing output audio context:', err);
        }
      }
      
      // Clean up any websocket connections
      if (liveAPI.connected && liveAPI.disconnect) {
        try {
          liveAPI.disconnect()
            .catch(err => console.error('[InterviewContext] Error disconnecting from API during unmount:', err));
        } catch (error) {
          console.error('[InterviewContext] Error during API disconnect on unmount:', error);
        }
      }
      
      console.log('[InterviewContext] Unmount cleanup completed');
    };
  }, [speechRecognitionEnabled]);
  
  // Function to toggle transcription type
  const toggleTranscriptionType = useCallback(() => {
    // Only allow toggling during development
    if (process.env.NODE_ENV !== 'development') {
      console.log('[InterviewContext] Transcription type toggling only available in development mode');
      return;
    }
    
    // Create a local reference to avoid circular imports
    const newType = ACTIVE_TRANSCRIPTION_TYPE === USER_TRANSCRIPTION_TYPE.GEMINI 
      ? USER_TRANSCRIPTION_TYPE.BROWSER 
      : USER_TRANSCRIPTION_TYPE.GEMINI;
    
    // This is a hack to update the constant at runtime
    (window as any).__ACTIVE_TRANSCRIPTION_TYPE = newType;
    Object.defineProperty(window, 'ACTIVE_TRANSCRIPTION_TYPE', {
      get: function() {
        return (window as any).__ACTIVE_TRANSCRIPTION_TYPE || ACTIVE_TRANSCRIPTION_TYPE;
      }
    });
    
    console.log(`[InterviewContext] Transcription type toggled to: ${newType}`);
    
    // Reset the speech recognition
    SpeechRecognitionManager.cleanup();
    
    // Reinitialize speech recognition with the new type
    setTimeout(() => {
      initializeSpeechRecognition();
    }, 500);
  }, [initializeSpeechRecognition]);
  
  // Create the context value object with all the state and functions
  const contextValue: InterviewContextType = {
    // Interview details
    interviewDetails,
    SetInterviewDetails: setInterviewDetails,
    userData,
    userInterviewId,
    SetUserInterviewId: setUserInterviewId,
    // Messages and transcript
    messages,
    sendUserMessage,
    downloadTranscript,
    
    // Speech recognition and transcription
    modelTranscript,
    
    // Timer related
    elapsedTime,
    formatTime,
    timerCompleted,
    setTimerCompleted,
    subscriptionMinutesConsumed, // Add this new property
    
    // Video/audio states
    isCameraEnabled,
    isMicEnabled,
    isScreenSharing,
    toggleCamera,
    toggleMicrophone,
    toggleScreenShare,
    updateVideoStream,
    
    // Device management
    videoDevices,
    audioDevices,
    selectedVideoDevice,
    selectedAudioDevice,
    handleDeviceChange,
    isExitingInterview,
    
    // UI states
    showAiAvatar,
    toggleFocus,
    isDeviceDropdownOpen,
    toggleDeviceDropdown,
    showMessageInput,
    toggleMessageInput,
    isMicMuted,
    audioLevel,
    isSpeaking: userSpeaking,
    
    // Connection status
    connected: actualConnected || liveAPI?.client?.ws?.readyState === WebSocket.OPEN, // Use actual WebSocket state
    connectionHealthy,
    extendedClient: liveAPI.client as unknown as ExtendedMultimodalLiveClient,
    
    // Error handling
    cameraError,
    connectionErrorVisible,
    connectionErrorType,
    setConnectionErrorVisible,
    setConnectionErrorType,
    
    // Feedback generation
    isGeneratingFeedback,
    isInterviewerThinking,
    setIsInterviewerThinking,
    
    // Interview control
    handleEndInterview,
    handleRestartInterview,
    showEndInterviewConfirmation,
    setShowEndInterviewConfirmation,
    showRestartConfirmation,
    setShowRestartConfirmation,
    confirmRestartInterview,
    confirmEndInterview,
    
    // Screen sharing control
    showStopScreenShareConfirmation,
    setShowStopScreenShareConfirmation,
    stopScreenSharing,
    
    // Refs
    videoRef,
    videoStream,
    userSpeakingRef,
    aiSpeakingRef,
    currentTranscriptRef,
    
    // Misc utilities
    getCameraStatusMessage,
    
    // Speech recognition state
    isListening,
    userSpeaking,
    // Add the toggle function
    toggleTranscriptionType,

  };
  
  // Monitor connection state and attempt reconnects if necessary
  useEffect(() => {
    // Check if component is still mounted
    if (!isMountedRef.current) return;
    
    // Skip if waiting for interval
    if (connectionAttemptInProgressRef.current) return;
    
    // Monitor actual WebSocket connection state
    const checkConnectionState = () => {
      // Check actual WebSocket state
      const isActuallyConnected = liveAPI?.client?.ws?.readyState === WebSocket.OPEN;
      
      // If state changed, update our state
      if (actualConnected !== isActuallyConnected) {
        console.log(`[InterviewContext] WebSocket connection state changed: ${isActuallyConnected ? 'CONNECTED' : 'DISCONNECTED'}`);
        setActualConnected(isActuallyConnected);
      }
      
      // If disconnected and we haven't reached max retries, try to reconnect
      if (!isActuallyConnected && reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
        console.log(`[InterviewContext] Detected disconnection, attempting to reconnect (attempt ${reconnectAttemptRef.current + 1}/${MAX_RECONNECT_ATTEMPTS})...`);
        
        // Attempt reconnection (doesn't need to be awaited)
        void initializeConnection();
      }
    };
    
    // Run initial check
    checkConnectionState();
    
    // Set up interval to periodically check connection state
    const intervalId = setInterval(checkConnectionState, 2000);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [actualConnected, initializeConnection, liveAPI?.client?.ws?.readyState]);
  
  // Add a dedicated useEffect to handle subscription minutes consumed state changes
  useEffect(() => {
    // Only run this effect when subscription minutes are consumed (when the state changes to true)
    if (subscriptionMinutesConsumed) {
      console.log('[InterviewContext] Handling subscription minutes consumed state change');
      
      // Use setTimeout to move this state update out of render phase
      // This prevents the "Cannot update a component while rendering a different component" error
      setTimeout(() => {
        // Ensure connection health is set to true to prevent reconnection indicators
        setConnectionHealthy(true);
      }, 0);
      
      // Make sure speech recognition is stopped
      if (speechRecognitionEnabled) {
      SpeechRecognitionManager.stop();
      }
      
      // Close any WebSocket connections that are stored on the window object
      if (window.mocktrailWsConnections && window.mocktrailWsConnections.length > 0) {
        console.log(`[InterviewContext] Closing ${window.mocktrailWsConnections.length} websocket connections due to minutes consumed...`);
        
        const wsConnections = [...window.mocktrailWsConnections]; // Create a copy to avoid modification issues
        
        wsConnections.forEach((ws, index) => {
          try {
            if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
              // Remove event handlers to prevent reconnection attempts
              ws.onclose = null;
              ws.onerror = null;
              ws.close(1000, "Subscription minutes consumed");
              console.log(`[InterviewContext] Closed websocket connection #${index} due to minutes consumed`);
            }
          } catch (err) {
            console.error(`[InterviewContext] Error closing websocket connection #${index}:`, err);
          }
        });
        
        // Clear the array
        window.mocktrailWsConnections = [];
      }
      
      // Clear any global timers except our elapsedTimer to ensure cleanup works properly
      if (window.altairTimers && window.altairTimers.length > 0) {
        console.log(`[InterviewContext] Clearing ${window.altairTimers.length} timers due to minutes consumed...`);
        window.altairTimers.forEach((timer, index) => {
          try {
            clearTimeout(timer);
            clearInterval(timer);
            console.log(`[InterviewContext] Cleared timer #${index}`);
          } catch (err) {
            console.error(`[InterviewContext] Error clearing timer #${index}:`, err);
          }
        });
        window.altairTimers = [];
      }
      
      // Make sure silence detection timeouts are cleared
      if (typeof window !== 'undefined' && window.messageFinalizationTimer) {
        clearTimeout(window.messageFinalizationTimer);
        window.messageFinalizationTimer = undefined;
      }
    }
  }, [subscriptionMinutesConsumed, speechRecognitionEnabled]);
  
  // Add a specific effect to handle isMicMuted state changes
  useEffect(() => {
    if (!speechRecognitionEnabled) {
      return;
    }
    
    // Skip during initial mount
    if (!isMountedRef.current) return;
    
    // Update the mute state in the SpeechRecognitionManager directly
    SpeechRecognitionManager.setMuted(isMicMuted);
    
    // Add debounce to prevent rapid state changes
    const debounceTimeout = setTimeout(() => {
      if (isMicMuted) {
        console.log('[InterviewContext] Microphone muted, pausing speech recognition');
        
        // If there's an active transcript, make sure to finalize it before muting
        if (currentTranscriptRef.current && currentTranscriptRef.current.trim().length > 0) {
          console.log('[InterviewContext] Finalizing transcript before muting:', currentTranscriptRef.current);
          handleUserTurnComplete(currentTranscriptRef.current);
        }
        
        // Wait a bit to let the finalization complete, then stop recognition
        setTimeout(() => {
          SpeechRecognitionManager.stop();
        }, 50);
      } else if (isMicEnabled && isListening) {
        console.log('[InterviewContext] Microphone unmuted, resuming speech recognition');
        
        // First stop existing recognition to clear browser state
        SpeechRecognitionManager.stop();
        
        // Reset the transcript to prevent old text from reappearing
        currentTranscriptRef.current = '';
        
        // Add a longer delay to avoid state conflicts
        setTimeout(() => {
          // Check if state is still valid after the delay
          if (isMicEnabled && !isMicMuted && isListening) {
            console.log('[InterviewContext] Restarting speech recognition after unmute');
            // Start with a clean state
            SpeechRecognitionManager.start();
          }
        }, 500);
      }
    }, 100); // Short debounce to prevent conflicts
    
    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [isMicMuted, isMicEnabled, isListening, handleUserTurnComplete, currentTranscriptRef, speechRecognitionEnabled]);
  
  return (
    <InterviewContext.Provider
      value={contextValue}
    >
      {children}
    </InterviewContext.Provider>
  );
};

// Custom hook to use the interview context
export const useInterview = (): InterviewContextType => {
  const context = useContext(InterviewContext);
  
  if (context === undefined) {
    throw new Error('useInterview must be used within an InterviewProvider');
  }
  
  return context;
}; 
